import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { BestValueTransition } from "@/components/BestValueTransition";
import { useAppData } from "@/data/AppDataContext";
import { useProductOptions } from "@/data/ProductOptionsContext";
import { useProductRegistry } from "@/data/ProductRegistryContext";
import { useCatalog } from "@/data/CatalogContext";
import { usePricing } from "@/data/PricingContext";
import { useBranding } from "@/data/BrandingContext";
import { calculatePrice, type ComplexityFactors, type PricingOverrides } from "@/data/pricingEngine";
import { deriveBuildSummary, deriveScope, type BuildSummaryItem } from "@/data/buildConfigMapping";
import { createConfigSnapshot } from "@/data/buildConfigMapping";
import type { ContractDocument } from "@/data/quoteTypes";
import { JobService } from "@/services/job.service";
import { QuoteService } from "@/services/quote.service";
import { LeadService } from "@/services/lead.service";
import { AccountingService } from "@/services/accounting.service";
import { ConsultationService, type ConsultationSessionData } from "@/services/consultation.service";
import { replaceJobMaterialsSnapshot } from "@/services/inventory.service";
import { ensureProductsForMaterialNames, getProducts as getRegistryProducts } from "@/services/productRegistry.service";
import { DEFAULT_CONFIG } from "@/components/ProjectConfigurator";
import { buildInventoryFromRegistryProducts, buildSkuCatalogFromRegistryProducts, generateMaterialList, toMaterialLineItems, computeMaterialsReadiness } from "@/data/skuMapping";
import type { MaterialRequirement, SkuItem } from "@/data/skuMapping";
import type { Lead } from "@/data/pipelineTypes";
import type { ConsultationProgress } from "@/data/opportunityTypes";
import { toUiQuote } from "@/types/mappers/quote.mapper";
import type {
  Job, ProjectType, ProjectConfiguration, LayoutType, EnclosureType,
  WallMeasurement, StructuralChanges, BathroomMaterial, HardwareFinish,
  ConsultationCategory, CustomAddOn, getVerticalFromCategory,
  MaterialsStatus,
} from "@/data/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { SignatureCapture } from "@/components/SignatureCapture";
import { formatPhoneNumber } from "@/lib/phoneFormat";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Check, CheckCircle, X,
  MapPin, Phone, User, Search, Eye, Ruler, Hammer,
  Paintbrush, DollarSign, FileSignature, Camera, Package,
  Square, Triangle, Grid2X2, Bath, AlertTriangle, ShowerHead,
  Percent, Presentation, Plus, Trash2, ChevronRight, Sparkles,
  CreditCard, Banknote, CalendarCheck, FileText, Shield,
  Mail, Layers, GlassWater, DoorOpen, Wrench, Grip, Palette,
  RectangleHorizontal, Info, Binoculars, Heart, Home, Star,
} from "lucide-react";
import { DesignBuildSummary, validateDesignStep } from "@/components/DesignBuildSummary";
import { DesignValidationDialog } from "@/components/DesignValidationDialog";
import { SendContractDialog } from "@/components/SendContractDialog";
import { generateContractPDF } from "@/utils/generateContractPDF";
import confetti from "canvas-confetti";
// import { config } from "process";
// import LeadDetail from "@/pages/LeadDetail";

/* ═══════════════════════════════════════════════════════
   Flow Step Definitions (9 Steps)
   ═══════════════════════════════════════════════════════ */

interface FlowStep {
  id: string;
  label: string;
  shortLabel: string;
  icon: React.ElementType;
  headline: string;
  subtext: string;
}

const FLOW_STEPS: FlowStep[] = [
  { id: "arrival",    label: "Arrival & Verification",  shortLabel: "Arrival",      icon: MapPin,         headline: "Welcome — Let's Get Started",                    subtext: "Confirm we're with the right person at the right place." },
  { id: "discovery",  label: "Project Discovery",       shortLabel: "Discovery",    icon: Search,         headline: "Tell Us About Your Vision",                      subtext: "What are you looking to improve? Let's understand your goals." },
  { id: "site",       label: "Present State",           shortLabel: "Site",         icon: Eye,            headline: "Let's Look at Your Current Space",               subtext: "We'll assess the layout and conditions to build the best plan." },
  { id: "measure",    label: "Measurements",            shortLabel: "Measure",      icon: Ruler,          headline: "Capturing Your Exact Dimensions",                subtext: "Precision measurements ensure a perfect fit for your new space." },
  { id: "structure",  label: "Modifications",           shortLabel: "Structure",    icon: Hammer,         headline: "Any Changes to the Structure?",                  subtext: "Let us know if anything needs to be moved, added, or modified." },
  { id: "design",     label: "Design Your Space",       shortLabel: "Design",       icon: Paintbrush,     headline: "Let's Design Something Beautiful",               subtext: "Choose the materials, colors, and features that match your style." },
  { id: "discounts",  label: "Special Savings",         shortLabel: "Savings",      icon: Heart,          headline: "Let's See If You Qualify for Savings",           subtext: "We honor those who've served and give back to our community." },
  { id: "price",      label: "Your Investment",         shortLabel: "Investment",   icon: Presentation,   headline: "",                                               subtext: "" },
  { id: "close",      label: "Finalize",                shortLabel: "Finalize",     icon: FileSignature,  headline: "",                                               subtext: "" },
];

/* ═══════════════════════════════════════════════════════
   Constants — Dynamic by Vertical
   ═══════════════════════════════════════════════════════ */

const CONSULTATION_CATEGORIES: { id: ConsultationCategory; label: string; description: string; icon: React.ElementType }[] = [
  { id: "Shower Remodel", label: "Walk-in Shower", description: "Modern, accessible shower conversion", icon: ShowerHead },
  { id: "Bath Conversion", label: "Bathtub", description: "Beautiful tub replacement or upgrade", icon: Bath },
  { id: "Full Bathroom Remodel", label: "Full Bathroom Remodel", description: "Complete transformation of your space", icon: Hammer },
  { id: "Walk-in Tub", label: "Walk-in Tub", description: "Safe, comfortable walk-in tub solution", icon: Bath },
  { id: "Windows", label: "Windows", description: "Energy-efficient window replacement", icon: Square },
  { id: "Doors", label: "Doors", description: "Premium entry or patio door upgrade", icon: DoorOpen },
];

const PAIN_POINTS_BY_VERTICAL: Record<string, { label: string; icon: React.ElementType }[]> = {
  bathroom: [
    { label: "Outdated look", icon: Eye },
    { label: "Hard to clean", icon: Sparkles },
    { label: "Accessibility concerns", icon: Heart },
    { label: "Leaking / water damage", icon: AlertTriangle },
    { label: "Mold or mildew", icon: AlertTriangle },
    { label: "Want more space", icon: Home },
    { label: "Safety concerns", icon: Shield },
    { label: "Poor lighting", icon: Star },
  ],
  windows: [
    { label: "Drafty / air leaks", icon: AlertTriangle },
    { label: "High energy bills", icon: DollarSign },
    { label: "Condensation issues", icon: AlertTriangle },
    { label: "Difficulty opening/closing", icon: Wrench },
    { label: "Outdated look", icon: Eye },
    { label: "Noise from outside", icon: Shield },
    { label: "UV damage to furniture", icon: Star },
    { label: "Security concerns", icon: Shield },
  ],
  doors: [
    { label: "Drafty / poor seal", icon: AlertTriangle },
    { label: "Hard to open/close", icon: Wrench },
    { label: "Outdated look", icon: Eye },
    { label: "Security concerns", icon: Shield },
    { label: "Weather damage", icon: AlertTriangle },
    { label: "Poor insulation", icon: Home },
    { label: "Accessibility needs", icon: Heart },
    { label: "Noise from outside", icon: Shield },
  ],
};

const LAYOUT_OPTIONS: { id: LayoutType; label: string; description: string; icon: React.ElementType; wallCount: number }[] = [
  { id: "Alcove", label: "Alcove (3-Wall)", description: "Standard 3-wall enclosed space", icon: Square, wallCount: 3 },
  { id: "Corner", label: "Corner (2-Wall)", description: "Two walls meeting at a corner", icon: Triangle, wallCount: 2 },
  { id: "Custom", label: "Custom / Open", description: "Non-standard layout", icon: Grid2X2, wallCount: 0 },
];

const ENCLOSURE_OPTIONS: { id: EnclosureType; label: string; description: string }[] = [
  { id: "Sliding Glass", label: "Sliding Glass Doors", description: "Space-saving sliding glass door" },
  { id: "Frameless Glass", label: "Frameless Glass Doors", description: "Premium frameless glass enclosure" },
  { id: "Curtain Rod", label: "Tension Rod", description: "Tension or wall-mounted curtain rod (default)" },
  { id: "Open", label: "Open (No Enclosure)", description: "No door or enclosure installed" },
  { id: "Semi-Frameless Glass", label: "Custom Glass", description: "Custom glass configuration" },
];

const HARDWARE_FINISHES: { id: HardwareFinish; label: string; swatch: string }[] = [
  { id: "Chrome", label: "Chrome", swatch: "bg-gradient-to-br from-gray-200 to-gray-400" },
  { id: "Brushed Nickel", label: "Brushed Nickel", swatch: "bg-gradient-to-br from-gray-300 to-gray-500" },
  { id: "Oil Rubbed Bronze", label: "Oil Rubbed Bronze", swatch: "bg-gradient-to-br from-amber-900 to-amber-700" },
  { id: "Matte Black", label: "Matte Black", swatch: "bg-gradient-to-br from-gray-800 to-gray-950" },
  { id: "Gold", label: "Gold", swatch: "bg-gradient-to-br from-yellow-400 to-yellow-600" },
  { id: "Multi", label: "Multi (Per-Item)", swatch: "bg-gradient-to-br from-gray-400 via-gray-600 to-gray-800" },
];

const OBSTRUCTION_OPTIONS = ["None / Not Applicable", "Bench", "Niche", "Soffit", "Shelf"];

const WINDOW_TYPES = ["Double Hung", "Casement", "Sliding", "Bay/Bow", "Picture", "Awning"];
const DOOR_ENTRY_TYPES = ["Single Entry", "Double Entry", "Sliding Patio", "French", "Storm"];
const DOOR_SIZES = ["30\"", "32\"", "36\"", "42\"", "48\"", "Custom"];

// const [currentSte, setCurrentStep] = useState(
//   LeadDetail.consultationProgress?.currentStep ?? 0
// )
// const [arrival, setArrival] = useState<ArrivalData>(
//   LeadDetail.consultationProgress?.arrivalData || {
//     withHomeowner: null,
//     name: lead.primaryName,
//     phone: lead.primaryPhone,
//     address: `${lead.address}, ${LeadDetail.city}, ${lead.state}, ${LeadDetail.zip}`,
//     secondaryName: lead.secondaryName || "",
//     secondaryPhone: lead.secondaryPhone || "",
//     primaryEmail: lead.primaryEmail || "",
//     secondaryEmail: lead.secondaryEmail || "",
//   }
// )

// const saveConsultationProgress = useCallback(() => {
//   const progress: ConsultationProgress = {
//     currentStep,
//     startedAt: lead.consultationProgress?.startedAt || new Date().toISOString(),
//     lastUpdated: new Date().toISOString(),
//     arrivalData: arrival,
//     discoveryData: discovery,
//     configData: config,
//     completedScopes: completedScopes,
//     closeStage,
//     selectedPayment,
//   };

//   setLeads(prev => prev.map(l => 
//     l.id === lead.id 
//       ? { ...l, consultationProgress: progress }
//       : l
//   ));
// }, [lead.id, currentStep, arrival, discovery, config, completedScopes, closeStage, selectedPayment]);

// const handleBack = () => {
//   saveConsultationProgress();
//   if (currentStep > 0) setCurrentStep(currentStep - 1);
// };


// const saveConsultationProgress = useCallback(async (step: number) => {
//   const progress: ConsultationProgress = {
//     currentStep: step,
//     startedAt: lead.consultationProgress?.startedAt || new Date().toISOString(),
//     lastUpdated: new Date().toISOString(),
//     arrivalData: arrival,
//     discoveryData: discovery,
//     configData: config,
//     completedScopes: completedScopes,
//     closeStage,
//     selectedPayment,
//   };
  
//   // Update local state
//   setLeads(prev => prev.map(l => 
//     l.id === lead.id 
//       ? { ...l, consultationProgress: progress }
//       : l
//   ));
// }, [lead.id, arrival, discovery, config, completedScopes, closeStage, selectedPayment]);

const STANDARD_SIZE_PRESETS = [
  { label: "30×60", baseWidth: "30", baseLength: "60", wallA: { width: "30", height: "96" }, wallB: { width: "60", height: "96" }, wallC: { width: "30", height: "96" } },
  { label: "32×60", baseWidth: "32", baseLength: "60", wallA: { width: "32", height: "96" }, wallB: { width: "60", height: "96" }, wallC: { width: "32", height: "96" } },
  { label: "34×60", baseWidth: "34", baseLength: "60", wallA: { width: "34", height: "96" }, wallB: { width: "60", height: "96" }, wallC: { width: "34", height: "96" } },
  { label: "36×60", baseWidth: "36", baseLength: "60", wallA: { width: "36", height: "96" }, wallB: { width: "60", height: "96" }, wallC: { width: "36", height: "96" } },
];

/* ═══════════════════════════════════════════════════════
   Helpers
   ═══════════════════════════════════════════════════════ */

function deriveProjectType(config: ProjectConfiguration): string {
  const sc = config.structuralChanges;
  if (sc.removingWalls || sc.addingWalls || sc.subfloorMod) return "Full Bathroom Remodel";
  if (sc.movingDrain || sc.movingPlumbing) return "Tub to Shower Conversion";
  return "Shower Remodel";
}

function getVertical(category: ConsultationCategory | ""): "bathroom" | "windows" | "doors" {
  if (category === "Windows") return "windows";
  if (category === "Doors") return "doors";
  return "bathroom";
}

function generateMaterialsFromConfig(config: ProjectConfiguration, allOptions: { id: string; displayName: string; category: string }[]) {
  const materials: { category: string; item: string; quantity: number; unit: string; source: string }[] = [];
  const wallCount = config.wallCount || 3;
  const walls = [config.wallMeasurements.wallA, config.wallMeasurements.wallB, ...(wallCount >= 3 ? [config.wallMeasurements.wallC] : [])].filter(w => w.width && w.height);
  const totalWallSqFt = walls.reduce((sum, w) => sum + (parseFloat(w.width) || 0) * (parseFloat(w.height) || 0) / 144, 0);

  if (config.selectedWallSystem) {
    const name = allOptions.find(o => o.id === config.selectedWallSystem)?.displayName || "Wall System";
    materials.push({ category: "Wall System", item: name, quantity: Math.ceil(totalWallSqFt), unit: "sq ft", source: "Design" });
  }
  if (config.bathroomMaterials.length > 0) {
    materials.push({ category: "Wall Material", item: config.bathroomMaterials.join(", "), quantity: Math.ceil(totalWallSqFt), unit: "sq ft", source: "Design" });
  }
  if (config.baseType || config.selectedBase) {
    const name = config.selectedBase ? (allOptions.find(o => o.id === config.selectedBase)?.displayName || config.baseType) : config.baseType;
    materials.push({ category: "Base / Pan", item: name || "Standard Base", quantity: 1, unit: "ea", source: "Design" });
  }
  if (config.enclosureType && config.enclosureType !== "Open") {
    materials.push({ category: "Enclosure", item: config.enclosureType, quantity: 1, unit: "ea", source: "Design" });
  }
   if (config.selectedGlass) materials.push({ category: "Glass", item: allOptions.find(o => o.id === config.selectedGlass)?.displayName || "Glass", quantity: 1, unit: "ea", source: "Design" });
   if (config.selectedDoor) materials.push({ category: "Door", item: allOptions.find(o => o.id === config.selectedDoor)?.displayName || "Door", quantity: 1, unit: "ea", source: "Design" });
   if (config.selectedFixture) {
     const name = allOptions.find(o => o.id === config.selectedFixture)?.displayName || "Fixture Package";
     materials.push({ category: "Fixtures", item: name, quantity: 1, unit: "set", source: "Design" });
   }
   config.selectedGrabBars.forEach(id => {
     const name = allOptions.find(o => o.id === id)?.displayName || "Grab Bar";
     materials.push({ category: "Accessories", item: name, quantity: 1, unit: "ea", source: "Design" });
   });
   config.selectedAddOns.forEach(id => {
     const name = allOptions.find(o => o.id === id)?.displayName || "Add-on";
     materials.push({ category: "Add-ons", item: name, quantity: 1, unit: "ea", source: "Design" });
   });
  config.customAddOns?.forEach(ca => materials.push({ category: "Custom Add-on", item: ca.name, quantity: 1, unit: "ea", source: "Custom" }));
  materials.push(
    { category: "Installation Supplies", item: "Silicone Sealant", quantity: 2, unit: "tube", source: "Auto-Bundle" },
    { category: "Installation Supplies", item: "Waterproof Membrane", quantity: Math.ceil(totalWallSqFt * 0.1) || 1, unit: "sq ft", source: "Auto-Bundle" },
    { category: "Installation Supplies", item: "Adhesive / Thin-set", quantity: Math.ceil(totalWallSqFt * 0.05) || 1, unit: "bag", source: "Auto-Bundle" },
    { category: "Installation Supplies", item: "Trim & Edge Pieces", quantity: wallCount * 2, unit: "ea", source: "Auto-Bundle" },
    { category: "Installation Supplies", item: "Drain Assembly", quantity: 1, unit: "ea", source: "Auto-Bundle" },
  );
  if (config.structuralChanges.movingDrain || config.structuralChanges.movingPlumbing) materials.push({ category: "Plumbing", item: "Plumbing Relocation Kit", quantity: 1, unit: "kit", source: "Structural" });
  if (config.structuralChanges.subfloorMod) materials.push({ category: "Structural", item: "Subfloor Repair Materials", quantity: 1, unit: "kit", source: "Structural" });
  return materials;
}

function normalizeLookupValue(value: string | null | undefined): string {
  return (value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, " ")
    .replace(/[^\w\s]/g, "");
}

function includesAny(value: string, tokens: string[]): boolean {
  return tokens.some((token) => value.includes(token));
}

function chooseCatalogMatch(
  lookup: string,
  key: string,
  config: ProjectConfiguration,
  catalog: SkuItem[],
): SkuItem | undefined {
  const direct = catalog.find((entry) => {
    const catalogLookup = normalizeLookupValue(entry.itemName);
    return catalogLookup.includes(lookup) || lookup.includes(catalogLookup);
  });
  if (direct) return direct;

  if (key === "glass" && includesAny(lookup, ["open", "no enclosure", "curtain rod", "tension rod"])) {
    return undefined;
  }

  if (key === "base" && includesAny(lookup, ["sentrel", "safety base", "base"])) {
    const width = (config.baseWidth || "").trim();
    const length = (config.baseLength || "").trim();
    const sizeCode = `${width}${length}`;
    if (sizeCode) {
      const sized = catalog.find((entry) => entry.sku === `BASE-SEN-${sizeCode}`);
      if (sized) return sized;
    }
    return catalog.find((entry) => entry.sku.startsWith("BASE-SEN-"));
  }

  if (key === "fixture" && includesAny(lookup, ["showerhead", "fixture", "standard"])) {
    const finish = normalizeLookupValue(config.hardwareFinish || "");
    if (finish.includes("brushed nickel")) {
      return catalog.find((entry) => entry.sku === "FIX-STD-BN");
    }
    return catalog.find((entry) => entry.sku === "FIX-STD-CHR");
  }

  if (key === "hardware" && includesAny(lookup, ["grab bar", "grabbar", "safety bar"])) {
    const digits = lookup.match(/\b(12|18|24|36)\b/)?.[1] || "";
    const preferredSku = digits ? `ACC-GRAB-${digits}` : "";
    if (preferredSku) {
      const exact = catalog.find((entry) => entry.sku === preferredSku);
      if (exact) return exact;
    }
    return catalog.find((entry) => entry.sku === "ACC-GRAB-18")
      || catalog.find((entry) => entry.sku === "ACC-GRAB-24")
      || catalog.find((entry) => entry.sku.startsWith("ACC-GRAB-"));
  }

  return undefined;
}

function buildFallbackRequirementsFromConfig(
  config: ProjectConfiguration,
  allOptions: Array<{ id: string; displayName: string }>,
  catalog: SkuItem[],
): { requirements: MaterialRequirement[]; unmappedItems: string[] } {
  const optionLabelById = new Map(allOptions.map((option) => [option.id, option.displayName]));
  const candidates: Array<{ key: string; raw: string; quantity: number }> = [
    { key: "walls", raw: config.selectedWallSystem || config.primaryMaterial || "", quantity: 1 },
    { key: "base", raw: config.selectedBase || config.baseType || "", quantity: 1 },
    { key: "glass", raw: config.selectedGlass || config.enclosureType || "", quantity: 1 },
    { key: "door", raw: config.selectedDoor || "", quantity: 1 },
    { key: "fixture", raw: config.selectedFixture || "", quantity: 1 },
    ...((config.selectedGrabBars || []).map((value) => ({ key: "hardware", raw: value, quantity: 1 }))),
    ...((config.selectedAddOns || []).map((value) => ({ key: "accessory", raw: value, quantity: 1 }))),
    ...((config.customAddOns || []).map((value) => ({ key: "accessory", raw: value.name || "", quantity: 1 }))),
  ];

  const out: MaterialRequirement[] = [];
  const unmappedItems: string[] = [];
  const seen = new Set<string>();

  candidates.forEach((candidate) => {
    const { key } = candidate;
    const raw = candidate.raw?.trim();
    if (!raw) return;

    const resolvedName = (optionLabelById.get(raw) || raw).trim();
    if (!resolvedName) return;
    const lookup = normalizeLookupValue(resolvedName);
    if (key === "glass" && includesAny(lookup, ["open", "no enclosure", "curtain rod", "tension rod"])) return;
    if (seen.has(lookup)) return;
    seen.add(lookup);

    const matchedCatalog = chooseCatalogMatch(lookup, key, config, catalog);

    if (!matchedCatalog?.sku) {
      unmappedItems.push(resolvedName);
      return;
    }
    const qty = Math.max(1, candidate.quantity || 1);
     out.push({
       sku: matchedCatalog.sku,
       itemName: matchedCatalog.itemName || resolvedName,
       category: matchedCatalog.category || "accessory",
       quantityRequired: qty,
       unitCost: matchedCatalog.unitCost || 0,
       unitPrice: matchedCatalog.unitPrice,
       quantityInStock: 0,
       availabilityStatus: "Not Available",
     });
  });

  return { requirements: out, unmappedItems };
}

/* ═══════════════════════════════════════════════════════
   Reusable Step Section Header
   ═══════════════════════════════════════════════════════ */

function StepSection({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-4">
      <p className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em]">{label}</p>
      {children}
    </div>
  );
}

function SelectionIndicator({ checked }: { checked: boolean }) {
  return (
    <div
      aria-hidden="true"
      className={`h-4 w-4 rounded-sm border flex items-center justify-center shrink-0 ${
        checked ? "border-primary bg-primary text-primary-foreground" : "border-muted-foreground/40 bg-background"
      }`}
    >
      {checked ? <Check className="h-3 w-3" /> : null}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   Appointment State
   ═══════════════════════════════════════════════════════ */

interface ArrivalData {
  withHomeowner: boolean | null;
  name: string;
  phone: string;
  address: string;
  secondaryName: string;
  secondaryPhone: string;
  primaryEmail: string;
  secondaryEmail: string;
}

interface DiscoveryData {
  category: ConsultationCategory | "";
  painPoints: string[];
  notes: string;
}

/* ═══════════════════════════════════════════════════════
   Multi-Scope Support
   ═══════════════════════════════════════════════════════ */

interface ProjectScope {
  id: string;
  category: ConsultationCategory;
  painPoints: string[];
  notes: string;
  config: ProjectConfiguration;
  noAddOns: boolean;
}

const VALID_CONSULTATION_CATEGORIES = new Set<ConsultationCategory>(
  CONSULTATION_CATEGORIES.map((category) => category.id),
);

function isConsultationCategory(value: unknown): value is ConsultationCategory {
  return typeof value === "string" && VALID_CONSULTATION_CATEGORIES.has(value as ConsultationCategory);
}

function normalizeDiscoveryData(
  value: ConsultationProgress["discoveryData"] | undefined,
): DiscoveryData {
  return {
    category: isConsultationCategory(value?.category) ? value.category : "",
    painPoints: Array.isArray(value?.painPoints) ? value!.painPoints : [],
    notes: typeof value?.notes === "string" ? value.notes : "",
  };
}

function normalizeCompletedScopes(
  value: ConsultationProgress["completedScopes"] | undefined,
): ProjectScope[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((scope): scope is NonNullable<ConsultationProgress["completedScopes"]>[number] =>
      Boolean(scope) && isConsultationCategory(scope.category),
    )
    .map((scope) => ({
      id: scope.id,
      category: scope.category as ConsultationCategory,
      painPoints: Array.isArray(scope.painPoints) ? scope.painPoints : [],
      notes: typeof scope.notes === "string" ? scope.notes : "",
      config: scope.config || { ...DEFAULT_CONFIG },
      noAddOns: Boolean(scope.noAddOns),
    }));
}

const BUNDLE_DISCOUNT_RATE = 0.05; // 5% bundle discount for 2+ scopes

/* ═══════════════════════════════════════════════════════
   Component
   ═══════════════════════════════════════════════════════ */

interface AppointmentFlowProps {
  lead: Lead;
  open: boolean;
  onClose: () => void;
}

export function AppointmentFlow({ lead, open, onClose }: AppointmentFlowProps) {
  const { setLeads, quotes, setQuotes, jobs, setJobs } = useAppData();
  const { getByCategory, options: allOptions } = useProductOptions();
  const { products: registryProducts, refreshProducts } = useProductRegistry();
  const { getProductsByCategory, getActiveVariants } = useCatalog();
  const { config: pricingConfig } = usePricing();
  const { branding } = useBranding();

  const [currentStep, setCurrentStep] = useState(lead.consultationProgress?.currentStep ?? 0);
  const [selectedSizePreset, setSelectedSizePreset] = useState<string>("");

  // Step 1 — Arrival
  const [arrival, setArrival] = useState<ArrivalData>(
  lead.consultationProgress?.arrivalData || {
    withHomeowner: null,
    name: lead.primaryName,
    phone: lead.primaryPhone,
    address: `${lead.address}, ${lead.city}, ${lead.state} ${lead.zip}`,
    secondaryName: lead.secondaryName || "",
    secondaryPhone: lead.secondaryPhone || "",
    primaryEmail: lead.primaryEmail || "",
    secondaryEmail: lead.secondaryEmail || "",
  }
);

  // Step 2 — Discovery - restore from saved progress
  const [discovery, setDiscovery] = useState<DiscoveryData>(
    normalizeDiscoveryData(lead.consultationProgress?.discoveryData),
  );

  // Steps 3-6 — Project Configuration
  const [config, setConfig] = useState<ProjectConfiguration>(() => 
    lead.consultationProgress?.configData || { ...DEFAULT_CONFIG }
  );

  // Steps 8+9 — Closing Presentation state
  type CloseStage = "hidden" | "standard" | "transition" | "pricing" | "photos" | "contract" | "confirmed";
  const ALL_CLOSE_STAGES: CloseStage[] = ["hidden", "standard", "transition", "pricing", "photos", "contract", "confirmed"];
  const CLOSE_LAYER_STAGES: CloseStage[] = ["photos", "contract", "confirmed"];

  const [closeStage, setCloseStage] = useState<CloseStage>(
    (lead.consultationProgress?.closeStage as CloseStage) || "hidden"
  );
  const [selectedPayment, setSelectedPayment] = useState<"cash" | "financing" | null>(
    lead.consultationProgress?.selectedPayment || null
  );
  const [permitRequired, setPermitRequired] = useState<boolean>(
    lead.consultationProgress?.permitRequired ?? false,
  );
  const [homeownerSig, setHomeownerSig] = useState("");
  const [repSig, setRepSig] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [rescissionAcknowledged, setRescissionAcknowledged] = useState(false);
  const [photosConfirmed, setPhotosConfirmed] = useState(false);
  const [designValidationOpen, setDesignValidationOpen] = useState(false);
  const [designSkipAcknowledged, setDesignSkipAcknowledged] = useState(false);
  const [noAddOns, setNoAddOns] = useState(false);
  const [promoRevealed, setPromoRevealed] = useState(false);
  const [bestValueSelection, setBestValueSelection] = useState<"cash" | "financing" | null>(null);
  const [bestValueRevealed, setBestValueRevealed] = useState(false);
  const [bestValueCalculating, setBestValueCalculating] = useState(false);
  const [pricingDeltaVisible, setPricingDeltaVisible] = useState<Record<string, boolean>>({});
  const [contractViewOpen, setContractViewOpen] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);

  // Multi-scope state
  const [completedScopes, setCompletedScopes] = useState<ProjectScope[]>(
    normalizeCompletedScopes(lead.consultationProgress?.completedScopes),
  );
  // Ref to track completedScopes for useCallback (prevents stale closures)
  const completedScopesRef = useRef(completedScopes);
  completedScopesRef.current = completedScopes;
  const [showScopeGate, setShowScopeGate] = useState(false);
  const [scopeDeleteConfirm, setScopeDeleteConfirm] = useState<string | null>(null);
  const [scopeEditingId, setScopeEditingId] = useState<string | null>(null);
  // Prevent double-click navigation
  const [isNavigating, setIsNavigating] = useState(false);
  const [consultationSessionId, setConsultationSessionId] = useState<string | null>(null);
  const [resolvedLeadId, setResolvedLeadId] = useState<string>("");
  const [resolvedQuoteId, setResolvedQuoteId] = useState<string>("");
  const hasUserEditedRef = useRef(false);
  const suppressEditTrackingRef = useRef(false);
  const sessionBootstrapRef = useRef(false);
  const initializedLeadIdRef = useRef<string | null>(null);

  const canonicalLeadId = useMemo(() => {
    const directLeadId = typeof lead.leadId === "string" ? lead.leadId.trim() : "";
    if (directLeadId) return directLeadId;

    const id = typeof lead.id === "string" ? lead.id.trim() : "";
    if (!id) return "";

    const matchedQuote = quotes.find((q) => String(q.id ?? "").trim() === id);
    if (typeof matchedQuote?.leadId === "string" && matchedQuote.leadId.trim().length > 0) {
      return matchedQuote.leadId.trim();
    }
    return id;
  }, [lead.id, lead.leadId, quotes]);

  const canonicalQuoteId = useMemo(() => {
    const directQuoteId = typeof lead.quoteId === "string" ? lead.quoteId.trim() : "";
    if (directQuoteId) return directQuoteId;

    const id = typeof lead.id === "string" ? lead.id.trim() : "";
    if (!id) return "";

    const matchedQuote = quotes.find((q) => String(q.id ?? "").trim() === id);
    if (matchedQuote?.id) return String(matchedQuote.id).trim();
    return "";
  }, [lead.id, lead.quoteId, quotes]);

  useEffect(() => {
    setResolvedLeadId(canonicalLeadId);
    setResolvedQuoteId(canonicalQuoteId);
  }, [canonicalLeadId, canonicalQuoteId]);

  useEffect(() => {
    let cancelled = false;
    const id = typeof lead.id === "string" ? lead.id.trim() : "";
    if (!open || !id) return () => { cancelled = true; };

    const leadLooksResolved = canonicalLeadId.length > 0 && canonicalLeadId !== id;
    if (leadLooksResolved && canonicalQuoteId) return () => { cancelled = true; };

    void (async () => {
      const { data } = await QuoteService.getById(id);
      if (cancelled || !data) return;
      const dbLeadId = typeof data.lead_id === "string" ? data.lead_id.trim() : "";
      const dbQuoteId = typeof data.id === "string" ? data.id.trim() : "";
      if (dbLeadId) setResolvedLeadId(dbLeadId);
      if (dbQuoteId) setResolvedQuoteId(dbQuoteId);
    })();

    return () => {
      cancelled = true;
    };
  }, [open, lead.id, canonicalLeadId, canonicalQuoteId]);

  const buildConsultationProgress = useCallback((): ConsultationProgress => ({
    currentStep,
    startedAt: lead.consultationProgress?.startedAt || new Date().toISOString(),
    lastUpdated: new Date().toISOString(),
    arrivalData: arrival,
    discoveryData: discovery,
    configData: config,
    completedScopes,
    permitRequired,
    closeStage,
    selectedPayment,
  }), [lead.consultationProgress?.startedAt, currentStep, arrival, discovery, config, completedScopes, permitRequired, closeStage, selectedPayment]);

  const hydrateFlowFromProgress = useCallback((progress: ConsultationProgress | null | undefined) => {
    suppressEditTrackingRef.current = true;
    setCurrentStep(progress?.currentStep ?? 0);
    setArrival(
      progress?.arrivalData || {
        withHomeowner: null,
        name: lead.primaryName,
        phone: lead.primaryPhone,
        address: `${lead.address}, ${lead.city}, ${lead.state} ${lead.zip}`,
        secondaryName: lead.secondaryName || "",
        secondaryPhone: lead.secondaryPhone || "",
        primaryEmail: lead.primaryEmail || "",
        secondaryEmail: lead.secondaryEmail || "",
      },
    );
    setDiscovery(normalizeDiscoveryData(progress?.discoveryData));
    setConfig(progress?.configData || { ...DEFAULT_CONFIG });
    setPermitRequired(Boolean(progress?.permitRequired));
    setCloseStage((progress?.closeStage as CloseStage) || "hidden");
    setSelectedPayment(progress?.selectedPayment || null);
    setCompletedScopes(normalizeCompletedScopes(progress?.completedScopes));

    // Reset transient UI-only state for a clean resume view.
    setSelectedSizePreset("");
    setHomeownerSig("");
    setRepSig("");
    setTermsAccepted(false);
    setRescissionAcknowledged(false);
    setPhotosConfirmed(false);
    setDesignValidationOpen(false);
    setDesignSkipAcknowledged(false);
    setNoAddOns(false);
    setPromoRevealed(false);
    setBestValueSelection(null);
    setBestValueRevealed(false);
    setBestValueCalculating(false);
    setPricingDeltaVisible({});
    setContractViewOpen(false);
    setSuccessModalOpen(false);
    setSendDialogOpen(false);
    setShowScopeGate(false);
    setScopeDeleteConfirm(null);
    setScopeEditingId(null);
    setIsNavigating(false);
    setTimeout(() => {
      suppressEditTrackingRef.current = false;
    }, 0);
  }, [lead.primaryName, lead.primaryPhone, lead.address, lead.city, lead.state, lead.zip, lead.secondaryName, lead.secondaryPhone, lead.primaryEmail]);

  // Save consultation progress when leaving
  const persistConsultationProgress = useCallback(async (progress: ConsultationProgress) => {
    const stepId = FLOW_STEPS[Math.min(Math.max(progress.currentStep, 0), FLOW_STEPS.length - 1)]?.id || "arrival";
    const sessionPayload = {
      consultationProgress: progress,
    };

    let sessionId = consultationSessionId;
    if (!sessionId) {
      const { data: quoteSession } = resolvedQuoteId
        ? await ConsultationService.getSessionByQuoteId(resolvedQuoteId)
        : { data: null };
      const { data: leadSession } = !quoteSession?.id && resolvedLeadId
        ? await ConsultationService.getSessionByLead(resolvedLeadId)
        : { data: null };
      const existingSession = quoteSession ?? leadSession;
      if (existingSession?.id) {
        sessionId = existingSession.id;
      } else {
        if (!resolvedLeadId) {
          console.warn("[AppointmentFlow] Cannot create consultation session: missing canonical lead id.");
          return;
        }
        const { data: createdSession, error: createErr } = await ConsultationService.createSession(
          resolvedLeadId,
          undefined,
          resolvedQuoteId || undefined,
        );
        if (createErr || !createdSession?.id) {
          console.warn("[AppointmentFlow] Failed to create consultation session:", createErr);
          return;
        }
        sessionId = createdSession.id;
      }
      setConsultationSessionId(sessionId);
    }

    const { error: persistErr } = await ConsultationService.updateProgress(
      sessionId,
      stepId,
      sessionPayload,
    );
    if (persistErr) {
      console.warn("[AppointmentFlow] Failed to persist consultation progress:", persistErr);
    }

    // Hard backfill: if session exists but quote_id is still null, resolve by lead_id from DB.
    if (sessionId && resolvedLeadId) {
      const { data: latestQuote } = await QuoteService.getLatestByLeadId(resolvedLeadId);
      const latestQuoteId = typeof latestQuote?.id === "string" ? latestQuote.id : "";
      if (latestQuoteId) {
        const { error: linkErr } = await ConsultationService.linkQuote(sessionId, latestQuoteId);
        if (!linkErr && latestQuoteId !== resolvedQuoteId) {
          setResolvedQuoteId(latestQuoteId);
        }
      }
    }
  }, [consultationSessionId, resolvedLeadId, resolvedQuoteId]);

  const ensureSessionQuoteLinked = useCallback(async (quoteId: string) => {
    if (!quoteId) return;

    let sessionId = consultationSessionId;
    if (!sessionId) {
      const { data: quoteSession } = await ConsultationService.getSessionByQuoteId(quoteId);
      if (quoteSession?.id) {
        sessionId = quoteSession.id;
      } else if (resolvedLeadId) {
        const { data: leadSession } = await ConsultationService.getSessionByLead(resolvedLeadId);
        if (leadSession?.id) sessionId = leadSession.id;
      }
      if (sessionId) setConsultationSessionId(sessionId);
    }

    if (!sessionId) return;
    const { error } = await ConsultationService.linkQuote(sessionId, quoteId);
    if (error) {
      console.warn("[AppointmentFlow] Failed to link session quote_id:", error);
    }
  }, [consultationSessionId, resolvedLeadId]);

  const saveConsultationProgress = useCallback(() => {
    if (sessionBootstrapRef.current) return;
    const progress = buildConsultationProgress();

    setLeads((prev) =>
      prev.map((l) =>
        l.id === lead.id ? { ...l, consultationProgress: progress } : l
      )
    );
    void persistConsultationProgress(progress);
  }, [lead.id, setLeads, buildConsultationProgress, persistConsultationProgress]);

  useEffect(() => {
    if (open) return;
    setConsultationSessionId(null);
    initializedLeadIdRef.current = null;
    sessionBootstrapRef.current = false;
  }, [open, resolvedLeadId, resolvedQuoteId]);

  useEffect(() => {
    if (!open) return;
    const initKey = `${lead.id}:${resolvedLeadId}:${resolvedQuoteId}`;
    if (initializedLeadIdRef.current === initKey) return;
    initializedLeadIdRef.current = initKey;
    sessionBootstrapRef.current = true;
    hasUserEditedRef.current = false;
    hydrateFlowFromProgress(lead.consultationProgress);
    let cancelled = false;

    async function loadPersistedSession() {
      try {
        const { data: quoteSession } = resolvedQuoteId
          ? await ConsultationService.getSessionByQuoteId(resolvedQuoteId)
          : { data: null };
        const { data: leadSession } = !quoteSession?.id && resolvedLeadId
          ? await ConsultationService.getSessionByLead(resolvedLeadId)
          : { data: null };
        const session = quoteSession ?? leadSession;
        if (cancelled) return;

        if (session?.id) {
          setConsultationSessionId(session.id);
          if (resolvedQuoteId && !session.quote_id) {
            void ConsultationService.linkQuote(session.id, resolvedQuoteId).catch((err) => {
              console.warn("[AppointmentFlow] Failed to backfill session quote_id:", err);
            });
          }
          if (hasUserEditedRef.current) return;
          const storedProgress = (session.project_config as Record<string, unknown> | null)?.consultationProgress as ConsultationProgress | undefined;
          if (storedProgress) {
            hydrateFlowFromProgress(storedProgress);
          } else {
            const idx = FLOW_STEPS.findIndex((flowStep) => flowStep.id === session.current_step);
            if (idx >= 0) {
              setCurrentStep(idx);
            }
          }
          return;
        }

        // Ensure there is a session row from the first open, so resume can work reliably.
        if (!resolvedLeadId) {
          console.warn("[AppointmentFlow] Cannot initialize consultation session: missing canonical lead id.");
          return;
        }
        const { data: createdSession, error: createErr } = await ConsultationService.createSession(
          resolvedLeadId,
          undefined,
          resolvedQuoteId || undefined,
        );
        if (createErr || !createdSession?.id) {
          console.warn("[AppointmentFlow] Failed to initialize consultation session on open:", createErr);
          return;
        }
        setConsultationSessionId(createdSession.id);
      } finally {
        if (!cancelled) {
          setTimeout(() => {
            sessionBootstrapRef.current = false;
          }, 0);
        }
      }
    }

    void loadPersistedSession();
    return () => {
      cancelled = true;
    };
  }, [open, lead.id, resolvedLeadId, resolvedQuoteId, hydrateFlowFromProgress]);

  useEffect(() => {
    if (!open) return;
    if (sessionBootstrapRef.current) return;
    if (suppressEditTrackingRef.current) return;
    hasUserEditedRef.current = true;
  }, [open, currentStep, arrival, discovery, config, completedScopes, permitRequired, closeStage, selectedPayment]);

  useEffect(() => {
    if (!open) return;
    if (sessionBootstrapRef.current) return;
    const timer = setTimeout(() => {
      void persistConsultationProgress(buildConsultationProgress());
    }, 400);
    return () => clearTimeout(timer);
  }, [open, currentStep, arrival, discovery, config, completedScopes, permitRequired, closeStage, selectedPayment, persistConsultationProgress, buildConsultationProgress]);

  const handleBack = () => {
    saveConsultationProgress();
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  // Branding colors
  const brandColor = `hsl(${branding.primaryHue} ${branding.primarySaturation}% ${branding.primaryLightness}%)`;
  const brandColorLight = `hsl(${branding.primaryHue} ${branding.primarySaturation}% ${branding.primaryLightness}% / 0.08)`;
  const brandColorMid = `hsl(${branding.primaryHue} ${branding.primarySaturation}% ${branding.primaryLightness}% / 0.15)`;

  // Confetti effect when success modal opens
  useEffect(() => {
    if (successModalOpen) {
      const duration = 2000;
      const end = Date.now() + duration;

      const frame = () => {
        confetti({
          particleCount: 3,
          angle: 60,
          spread: 55,
          origin: { x: 0, y: 0.7 },
          colors: [brandColor, '#10b981', '#f59e0b'],
        });
        confetti({
          particleCount: 3,
          angle: 120,
          spread: 55,
          origin: { x: 1, y: 0.7 },
          colors: [brandColor, '#10b981', '#f59e0b'],
        });

        if (Date.now() < end) {
          requestAnimationFrame(frame);
        }
      };
      frame();
    }
  }, [successModalOpen, brandColor]);

  const step = FLOW_STEPS[currentStep];
  const progress = ((currentStep + 1) / FLOW_STEPS.length) * 100;
  const vertical = getVertical(discovery.category);
  const isBathroom = vertical === "bathroom";

  // Determine wall system id for dynamic options
  const wallSystemId = config.bathroomMaterials[0] || "";
  const isWalkInTub = discovery.category === "Walk-in Tub";

  /* ── Config helpers ── */
  const update = <K extends keyof ProjectConfiguration>(key: K, value: ProjectConfiguration[K]) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  const updateWall = (wall: "wallA" | "wallB" | "wallC", field: keyof WallMeasurement, value: string) => {
    setConfig(prev => ({
      ...prev,
      wallMeasurements: { ...prev.wallMeasurements, [wall]: { ...prev.wallMeasurements[wall], [field]: value } },
    }));
  };

  const updateStructural = (field: keyof StructuralChanges, value: boolean | string) => {
    setConfig(prev => ({ ...prev, structuralChanges: { ...prev.structuralChanges, [field]: value } }));
  };

  const toggleArray = (key: "selectedGrabBars" | "selectedAddOns" | "obstructions", id: string) => {
    setConfig(prev => {
      const arr = prev[key] as string[];
      return { ...prev, [key]: arr.includes(id) ? arr.filter(x => x !== id) : [...arr, id] };
    });
  };

  const setWallSystem = (systemId: string) => {
    setConfig(prev => ({
      ...prev,
      bathroomMaterials: [systemId as BathroomMaterial],
      wallColor: "",
      baseType: "",
      selectedBase: "",
    }));
  };

  const addCustomAddOn = () => {
    setConfig(prev => ({
      ...prev,
      customAddOns: [...(prev.customAddOns || []), { id: crypto.randomUUID(), name: "", priceOverride: "" }],
    }));
  };

  const updateCustomAddOn = (id: string, field: "name" | "priceOverride", value: string) => {
    setConfig(prev => ({
      ...prev,
      customAddOns: (prev.customAddOns || []).map(a => a.id === id ? { ...a, [field]: value } : a),
    }));
  };

  const removeCustomAddOn = (id: string) => {
    setConfig(prev => ({
      ...prev,
      customAddOns: (prev.customAddOns || []).filter(a => a.id !== id),
    }));
  };

  const applySizePreset = (preset: typeof STANDARD_SIZE_PRESETS[0]) => {
    setSelectedSizePreset(preset.label);
    setConfig(prev => ({
      ...prev,
      baseWidth: preset.baseWidth,
      baseLength: preset.baseLength,
      wallMeasurements: {
        ...prev.wallMeasurements,
        wallA: { ...preset.wallA },
        wallB: { ...preset.wallB },
        wallC: { ...preset.wallC },
      },
    }));
  };

  /* ── Pricing — Multi-scope aware ── */
  const pricingOverrides: PricingOverrides = {
    systemBasePrices: pricingConfig.systemBasePrices,
    dimensionPresets: pricingConfig.dimensionPresets,
    materialMultipliers: pricingConfig.materialMultipliers,
    complexityAdders: pricingConfig.complexityAdders,
  };
  const complexity: ComplexityFactors = {
    laborComplexity: config.laborComplexity || "standard",
    plumbingRelocation: config.plumbingRelocation || false,
    customModifications: config.customModifications || false,
  };
  const priceBreakdown = useMemo(() =>
    calculatePrice(config, allOptions, complexity, parseFloat(config.manualAdjustment) || 0, config.manualAdjustmentNotes || "", pricingOverrides),
    [config, allOptions]
  );

  // Calculate price for each completed scope
  const completedScopePrices = useMemo(() => {
    return completedScopes.map(s => {
      const sComplexity: ComplexityFactors = {
        laborComplexity: s.config.laborComplexity || "standard",
        plumbingRelocation: s.config.plumbingRelocation || false,
        customModifications: s.config.customModifications || false,
      };
      const breakdown = calculatePrice(s.config, allOptions, sComplexity, parseFloat(s.config.manualAdjustment) || 0, s.config.manualAdjustmentNotes || "", pricingOverrides);
      const customTotal = (s.config.customAddOns || []).reduce((sum, a) => sum + (parseFloat(a.priceOverride) || 0), 0);
      return breakdown.finalTotal + customTotal;
    });
  }, [completedScopes, allOptions, pricingOverrides]);

  // All scopes combined = completed scopes + current active scope (if on pricing step)
  const allScopes = useMemo(() => {
    if (completedScopes.length === 0) return [];
    return completedScopes;
  }, [completedScopes]);

  const isMultiScope = completedScopes.length > 1;

  const discountTotal = useMemo(() => {
    let d = 0;
    if (config.discounts?.military) d += 500;
    if (config.discounts?.senior) d += 300;
    if (config.discounts?.frontliner) d += 300;
    return d;
  }, [config.discounts]);

  const customAddOnTotal = useMemo(() => {
    return (config.customAddOns || []).reduce((sum, a) => sum + (parseFloat(a.priceOverride) || 0), 0);
  }, [config.customAddOns]);

  // Combined pricing across all completed scopes
  const combinedScopeTotal = useMemo(() => completedScopePrices.reduce((s, p) => s + p, 0), [completedScopePrices]);
  const bundleDiscount = isMultiScope ? Math.round(combinedScopeTotal * BUNDLE_DISCOUNT_RATE) : 0;
  const standardPrice = combinedScopeTotal > 0 ? combinedScopeTotal - bundleDiscount : priceBreakdown.finalTotal + customAddOnTotal;
  const retailPrice = standardPrice - discountTotal;
  const promoPrice = Math.round(retailPrice * 0.83);
  const ivpCashPrice = Math.round(promoPrice * 0.88);
  const ivpFinancePrice = Math.round(promoPrice * 0.93);
  const safeCashPrice = Math.min(ivpCashPrice, promoPrice);
  const safeFinancePrice = Math.min(ivpFinancePrice, promoPrice);
  const chosenPrice = selectedPayment === "cash" ? safeCashPrice : selectedPayment === "financing" ? safeFinancePrice : retailPrice;
  const depositAmount = selectedPayment === "financing" ? 0 : Math.round(chosenPrice * 0.5);

  const promoExpiration = useMemo(() => {
    const now = new Date();
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return endOfMonth.toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
  }, []);

  const oneYearExpiration = useMemo(() => {
    const now = new Date();
    const nextYear = new Date(now.getFullYear() + 1, now.getMonth(), now.getDate());
    return nextYear.toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
  }, []);

  const activeDiscountLabels = useMemo(() => {
    const labels: string[] = [];
    if (config.discounts?.military) labels.push("military");
    if (config.discounts?.senior) labels.push("senior");
    if (config.discounts?.frontliner) labels.push("frontliner");
    return labels;
  }, [config.discounts]);

  const advanceCloseStage = useCallback((to?: CloseStage) => {
    if (to) { setCloseStage(to); return; }
    const idx = ALL_CLOSE_STAGES.indexOf(closeStage);
    if (idx < ALL_CLOSE_STAGES.length - 1) setCloseStage(ALL_CLOSE_STAGES[idx + 1]);
  }, [closeStage]);

  const goBackCloseStage = useCallback(() => {
    const idx = ALL_CLOSE_STAGES.indexOf(closeStage);
    if (idx > 0) setCloseStage(ALL_CLOSE_STAGES[idx - 1]);
  }, [closeStage]);

  const isCloseLayer = CLOSE_LAYER_STAGES.includes(closeStage);
  const closeStageIndex = ALL_CLOSE_STAGES.indexOf(closeStage);
  const closeDotCount = ALL_CLOSE_STAGES.length - 1;
  const closeProgress = closeStageIndex;

  const buildItems: BuildSummaryItem[] = useMemo(() => {
    return deriveBuildSummary(config, allOptions);
  }, [config, allOptions]);

  const scope = useMemo(() => {
    return deriveScope(config);
  }, [config]);

  const ITEM_ICONS: Record<string, React.ElementType> = {
    System: ShowerHead, Base: RectangleHorizontal, "Base Size": RectangleHorizontal,
    "Base Type": RectangleHorizontal, "Base Style": RectangleHorizontal,
    Material: Palette, "Wall System": Layers, Glass: GlassWater,
    Door: DoorOpen, Fixtures: Wrench, "Grab Bars": Grip, "Add-ons": Sparkles,
    "Wall Color": Palette, "Base Color": Palette, "Color Selection": Palette,
  };
  const getItemIcon = (label: string): React.ElementType => ITEM_ICONS[label] || CheckCircle;
  const canSign = homeownerSig && repSig && termsAccepted && rescissionAcknowledged;

  /* ── Step Validation ── */
  const isStepValid = useCallback((stepId: string): boolean => {
    switch (stepId) {
      case "arrival": return arrival.withHomeowner === true && !!arrival.name.trim();
      case "discovery": return !!discovery.category;
      case "site":
        if (!isBathroom) return true;
        return !!config.layoutType && config.obstructions.length > 0;
      case "measure":
        if (!isBathroom) return true;
        return !!(config.wallMeasurements.wallA.width && config.wallMeasurements.wallA.height && config.wallMeasurements.wallB.width && config.wallMeasurements.wallB.height);
      case "structure": return true;
      case "design":
        if (!isBathroom) return true;
        return config.bathroomMaterials.length > 0;
      case "discounts": return true;
      case "price": return true;
      case "close": return true;
      default: return true;
    }
  }, [arrival, discovery, config, isBathroom]);

  const canAdvance = isStepValid(step.id);

  /* ── Save current scope to completedScopes ── */
  const saveCurrentScope = useCallback(() => {
    // Check if scope already exists (prevent duplicates when re-editing existing scope)
    // Use ref to access current value without triggering callback re-creation
    const existingScope = completedScopesRef.current.find(s =>
      s.category === discovery.category
    );
    if (existingScope) {
      console.log("[Scope] Already exists for this category, skipping duplicate save");
      return existingScope;
    }

    const scopeEntry: ProjectScope = {
      id: crypto.randomUUID(),
      category: discovery.category as ConsultationCategory,
      painPoints: discovery.painPoints,
      notes: discovery.notes,
      config: { ...config, autoProjectType: config.autoProjectType || deriveProjectType(config) },
      noAddOns,
    };
    setCompletedScopes(prev => [...prev, scopeEntry]);
    return scopeEntry;
  }, [discovery, config, noAddOns]);

  const handleAddAnotherScope = () => {
    // Prevent double-click
    if (isNavigating) return;
    setIsNavigating(true);
    // Reset discovery + config for fresh scope
    setDiscovery({ category: "", painPoints: [], notes: "" });
    setConfig({ ...DEFAULT_CONFIG });
    setNoAddOns(false);
    setDesignSkipAcknowledged(false);
    setSelectedSizePreset("");
    setPricingDeltaVisible({});
    setShowScopeGate(false);
    setCurrentStep(1); // Go back to Discovery (step index 1)
    // Reset navigation lock
    setTimeout(() => setIsNavigating(false), 100);
  };

  const handleProceedToPricing = () => {
    // Prevent double-click
    if (isNavigating) return;
    setIsNavigating(true);
    setShowScopeGate(false);
    setCurrentStep(FLOW_STEPS.findIndex(s => s.id === "discounts"));
    // Reset navigation lock
    setTimeout(() => setIsNavigating(false), 100);
  };

  const handleRemoveScope = (scopeId: string) => {
    setCompletedScopes(prev => prev.filter(s => s.id !== scopeId));
  };

  const handleNext = () => {
    // Prevent double-click causing multiple scope saves
    if (isNavigating) return;
    setIsNavigating(true);
    if (currentStep < FLOW_STEPS.length - 1 && canAdvance) {
      if (step.id === "structure") {
        const autoType = deriveProjectType(config);
        setConfig(prev => ({
          ...prev,
          autoProjectType: autoType,
          plumbingRelocation: prev.structuralChanges.movingDrain || prev.structuralChanges.movingPlumbing,
          customModifications: prev.structuralChanges.removingWalls || prev.structuralChanges.addingWalls || prev.structuralChanges.subfloorMod,
          laborComplexity: (prev.structuralChanges.removingWalls || prev.structuralChanges.addingWalls) ? "high" : prev.structuralChanges.movingDrain ? "moderate" : "standard",
        }));
      }
      if (step.id === "design") {
        // Validate design for bathroom
        if (isBathroom && !designSkipAcknowledged) {
          const validation = validateDesignStep(config, isBathroom);
          if (!validation.canProceed) {
            toast.error("Please complete all required selections before proceeding.", {
              description: `Missing: ${validation.criticalMissing.join(", ")}`,
            });
            return;
          }
          const filteredRecommended = noAddOns
            ? validation.recommendedMissing.filter(item => item !== "Add-ons")
            : validation.recommendedMissing;
          if (filteredRecommended.length > 0) {
            setDesignValidationOpen(true);
            return;
          }
        }
        // Check if scope already exists for this category
        const scopeExists = completedScopesRef.current.some(s => s.category === discovery.category);
        if (scopeExists) {
          // Skip scope gate, go directly to pricing
          setShowScopeGate(false);
          setCurrentStep(FLOW_STEPS.findIndex(s => s.id === "discounts"));
          setTimeout(() => setIsNavigating(false), 100);
          return;
        }
        // Save current scope and show scope gate
        saveCurrentScope();
        setShowScopeGate(true);
        // Reset navigation lock after showing scope gate
        setTimeout(() => setIsNavigating(false), 100);
        return;
      }
      setDesignSkipAcknowledged(false);
      setCurrentStep(currentStep + 1);
      setIsNavigating(false);
    }
  };

  const handleDesignContinueAnyway = () => {
    setDesignSkipAcknowledged(true);
    setDesignValidationOpen(false);
    console.log("[Audit] Design validation skipped:", {
      timestamp: new Date().toISOString(),
      missingItems: validateDesignStep(config, isBathroom).recommendedMissing,
    });
    // Save scope and show scope gate (same as normal design advance)
    saveCurrentScope();
    setShowScopeGate(true);
    // Reset navigation lock after showing scope gate
    setTimeout(() => setIsNavigating(false), 100);
  };

  // Directly skip scope gate and go straight to pricing (bypass review screen)
  const handleProceedDirectlyToPricing = () => {
    if (isNavigating) return;
    setIsNavigating(true);
    setDesignSkipAcknowledged(true);
    setDesignValidationOpen(false);
    setShowScopeGate(false);
    console.log("[Audit] Design validation skipped - direct to pricing:", {
      timestamp: new Date().toISOString(),
      missingItems: validateDesignStep(config, isBathroom).recommendedMissing,
    });
    setCurrentStep(FLOW_STEPS.findIndex(s => s.id === "discounts"));
    setTimeout(() => setIsNavigating(false), 100);
  };

  // const handleBack = () => { if (currentStep > 0) setCurrentStep(currentStep - 1); };

  const handleBestValueSelect = (option: "cash" | "financing") => {
    setBestValueSelection(option);
    setBestValueCalculating(true);
    setTimeout(() => {
      setBestValueCalculating(false);
      setBestValueRevealed(true);
    }, 2200);
  };

  /* ── Close / Sign Contract ── */
  const handleSignContract = useCallback(() => {
    const snapshot = createConfigSnapshot(config);
    const now = new Date().toISOString();
    const contract: ContractDocument = {
      id: `CTR-${Date.now()}`,
      quoteId: "",
      customerName: arrival.name,
      customerEmail: arrival.primaryEmail,
      customerAddress: arrival.address,
      contractTotal: chosenPrice,
      homeownerSignature: homeownerSig,
      homeownerSignedAt: now,
      repSignature: repSig,
      repSignedAt: now,
      termsAccepted: true,
      rescissionAcknowledged: true,
      status: "Signed",
      createdAt: now,
      approvedAt: now,
      ownershipType: "Homeowner",
      poaDocumentUploaded: false,
      hoaInvolved: false,
      selectedOptionsApproved: true,
      configSnapshotHash: snapshot.hash,
      configSnapshotAt: snapshot.snapshotAt,
      paymentMethod: selectedPayment === "financing" ? "Financing" : "Cash",
      depositAmount,
    };
    void handleMarkSoldWithContract(contract);
  }, [config, chosenPrice, homeownerSig, repSig, selectedPayment, depositAmount]);

  /* ── Close / Mark Sold ── */
  const handleMarkSoldWithContract = async (contract: ContractDocument) => {
    // Use first completed scope's config as the primary config
    const primaryScope = completedScopes[0];
    const primaryConfig = primaryScope?.config || config;
    const derivedType = primaryConfig.autoProjectType || deriveProjectType(primaryConfig);
    const allScopeLabels = completedScopes.map(s => s.category).join(" + ");
    const finalConfig: ProjectConfiguration = {
      ...primaryConfig,
      autoProjectType: derivedType,
      consultationCategory: primaryScope?.category || discovery.category,
      basePrice: String(chosenPrice),
      status: "accepted",
      lastUpdated: new Date().toISOString(),
    };
    const makeMaterialSelection = (products: typeof registryProducts) => {
      const skuCatalog = buildSkuCatalogFromRegistryProducts(products);
      const inventory = buildInventoryFromRegistryProducts(products);
      const generatedRequirements = generateMaterialList(finalConfig, undefined, skuCatalog, inventory);
      const fallbackRequirements = buildFallbackRequirementsFromConfig(finalConfig, allOptions, skuCatalog);
      const usesFallback = generatedRequirements.length === 0;
      const materialRequirements = usesFallback
        ? fallbackRequirements.requirements
        : generatedRequirements;
      
      const materialLineItems = toMaterialLineItems(materialRequirements);
      const unmappedItems = fallbackRequirements.unmappedItems;
      return { materialRequirements, materialLineItems, unmappedItems };
    };
    let { materialRequirements, materialLineItems, unmappedItems } = makeMaterialSelection(registryProducts);
    if (registryProducts.length === 0) {
      toast.error("Cannot close consultation: Product Registry is empty", {
        description: "Load/seed DB products first so SKU mappings can resolve.",
      });
      return;
    }
    if (unmappedItems.length > 0) {
      try {
        const ensured = await ensureProductsForMaterialNames(
          unmappedItems.map((name) => ({ name })),
        );
        if (ensured.created > 0) {
          await refreshProducts();
        }
        ({ materialRequirements, materialLineItems, unmappedItems } = makeMaterialSelection(
          ensured.created > 0 ? await getRegistryProducts() : registryProducts,
        ));
      } catch (error) {
        console.error("[AppointmentFlow] Auto-mapping unmapped materials failed:", error);
      }
    }
    if (unmappedItems.length > 0) {
      toast.error("Cannot close consultation: unmapped materials", {
        description: `Missing SKU mappings for: ${unmappedItems.slice(0, 4).join(", ")}${unmappedItems.length > 4 ? "..." : ""}`,
      });
      return;
    }
    if (materialLineItems.length === 0) {
      toast.error("Cannot close consultation: no mapped SKU line items");
      return;
    }
    const materialsReadiness = computeMaterialsReadiness(materialRequirements);
const materialsStatusMap: Record<string, MaterialsStatus> = {
       "Ready": "Ready",
       "Partially Available": "Missing Items",
       "Not Available": "Not Ready",
     };

    const nowIso = new Date().toISOString();
    const leadCustomerId = (lead as Lead & { customerId?: string | null }).customerId || null;
    if (!leadCustomerId) {
      toast.error("Unable to save quote before conversion", {
        description: "Lead is missing customer linkage. Please complete intake/customer link first.",
      });
      return;
    }

    // Save quote/config first — conversion should follow QuoteDetail persistence order.
    const quoteUpsertPayload: Record<string, unknown> = {
      quote_name: `${lead.primaryName} ${isMultiScope ? allScopeLabels : derivedType}`.trim(),
      customer_name: lead.primaryName,
      customer_email: arrival.primaryEmail || lead.primaryEmail || null,
      customer_phone: lead.primaryPhone || null,
      customer_address: arrival.address || null,
      lead_source: lead.leadSource || null,
      status: "Closed Won",
      notes: completedScopes.map(s => s.notes).filter(Boolean).join(" | ") || discovery.notes || null,
      total_estimate: chosenPrice,
      won_at: nowIso,
      locked_at: nowIso,
      locked_by: "sales",
      customer_id: leadCustomerId,
      project_config: finalConfig,
      contract: { ...contract, quoteId: "" },
      deposit: {
        amount: depositAmount,
        method: selectedPayment === "financing" ? "Financing" : "Cash / Check",
        date: nowIso,
        notes: "Captured from consultation close flow",
      },
      scope_of_work: scope || null,
      scenarios: [],
    };

    if (!resolvedLeadId) {
      toast.error("Unable to save quote before conversion", {
        description: "Resolved lead id is missing for this consultation session.",
      });
      return;
    }
    const { data: persistedQuote, error: quotePersistError } = await QuoteService.upsertByLeadId(resolvedLeadId, quoteUpsertPayload);
    if (quotePersistError || !persistedQuote?.id) {
      console.error("[AppointmentFlow] Failed to persist quote before conversion:", quotePersistError);
      toast.error("Failed to save quote before conversion");
      return;
    }

    const quoteId = persistedQuote.id;
    setResolvedQuoteId(quoteId);
    await ensureSessionQuoteLinked(quoteId);
    const quoteUi = toUiQuote(persistedQuote);
setQuotes(prev => {
       const existingIdx = prev.findIndex(q => q.id === quoteUi.id);
       if (existingIdx >= 0) {
         const next = [...prev];
         next[existingIdx] = quoteUi;
         return next;
       }
       return [...prev, quoteUi];
     });

    const scopeSummary = isMultiScope
      ? completedScopes.map(s => `${s.config.bathroomMaterials?.[0] || ""} ${s.category}`.trim()).join(" + ")
      : `${primaryConfig.bathroomMaterials[0] || ""} ${derivedType}`.trim();
    const newJob: Job = {
      id: crypto.randomUUID(),
      jobNumber: "",
      createdAt: nowIso.slice(0, 10),
      updatedAt: nowIso.slice(0, 10),
      customerName: lead.primaryName,
      customerId: leadCustomerId,
      address: { street: lead.address, city: lead.city, state: lead.state, zip: lead.zip },
      phone: lead.primaryPhone,
      email: arrival.primaryEmail || lead.primaryEmail,
      secondaryContactName: arrival.secondaryName || lead.secondaryName,
      secondaryContactPhone: arrival.secondaryPhone || lead.secondaryPhone,
      secondaryContactEmail: arrival.secondaryEmail || lead.secondaryEmail,
      projectType: (isMultiScope ? "Multi-Scope" : derivedType) as ProjectType,
      assignedCrew: "Unassigned",
      assignmentType: "Unassigned",
      scheduledInstallDate: "",
      permitRequired,
      permitStatus: permitRequired ? "Submitted" : "Not Required",
      orderStatus: "Not Ordered",
      materialsStatus: materialsStatusMap[materialsReadiness] || "Not Ready",
      dispatchStatus: "Not Ready",
      contractorAssignmentStatus: "Unassigned",
      currentStage: "Sale Closed",
      notes: completedScopes.map(s => s.notes).filter(Boolean).join(" | "),
      issueFlag: false,
      scopeSummary,
      payoutAmount: chosenPrice,
      financials: {
        contractValue: chosenPrice,
        estimatedMaterialCost: 0,
        estimatedLaborCost: 0,
        subcontractorPayout: 0,
        marginEstimate: 0,
        paymentStatus: selectedPayment === "financing" ? "Paid in Full" : "Not Invoiced",
        payoutStatus: "Not Submitted",
        // For financing: mark as paid in full since financing institution pays
        financing: selectedPayment === "financing" ? {
          financingStatus: "approved",
          financingApprovedAmount: chosenPrice,
        } : undefined,
        // Payment plan for financing: 0 deposit, full amount financed
        paymentPlan: selectedPayment === "financing" ? {
          type: "financed",
          requiresDeposit: false,
          depositAmount: 0,
          lineItems: [
            {
              id: crypto.randomUUID(),
              kind: "final",
              method: "financed",
              amount: chosenPrice,
              status: "approved",
            },
          ],
        } : undefined,
      },
      assignedSubcontractor: "",
      targetInstallDate: "",
      activityHistory: [
        { id: crypto.randomUUID(), date: new Date().toISOString(), action: "Job created from consultation flow", user: lead.assignedRep, noteType: "system" },
      ],
      photos: [],
      qaSubmitted: false,
      materialReadiness: "Not Ordered",
      materialReadinessNotes: materialsReadiness !== "Ready" ? `${materialLineItems.filter(m => m.missingItems > 0).length} items need procurement` : "",
      missingItemsList: materialLineItems.filter(m => m.missingItems > 0).map(m => m.itemName).join(", "),
      procurementStatus: "Not Ordered",
      warehouseStatus: "Not Received",
      branch: "Tampa",
      region: "West Florida",
      salesRep: lead.assignedRep,
      closeoutStatus: "Not Started",
      finalDocumentationComplete: false,
      customerSignoff: false,
      closeoutNotes: "",
      rescissionStatus: "In Rescission",
      rescissionEndsAt: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(),
      convertedFromQuoteId: quoteId,
      projectConfig: finalConfig,
      materialsList: materialLineItems,
      contractDocument: contract,
      scopeLocked: true,
      intakeApproved: false,
      permitDetail: permitRequired
        ? {
          permitType: "Standard Residential",
          municipality: "",
          submittedDate: "",
          detailedStage: "Permit Required",
          lastUpdate: nowIso,
          permitNotes: "Permit enabled during consultation.",
          assignedCoordinator: "",
          blockers: "",
          targetApprovalDate: "",
        }
        : undefined,
      assignedSubcontractorId: ""
    };
    const { data: savedJob, error: saveJobError } = await JobService.create(newJob);
    if (saveJobError) {
      console.error("[AppointmentFlow] Failed to persist created job:", saveJobError);
      toast.error("Failed to create job in database");
      return;
    }
    if (materialLineItems.length > 0) {
      await replaceJobMaterialsSnapshot({
        id: savedJob.id,
        jobNumber: savedJob.jobNumber,
        customerName: savedJob.customerName,
        currentStage: savedJob.currentStage,
        materialsList: materialLineItems,
      });
    }

    if (depositAmount > 0) {
      const { invoice, error: invoiceError } = await AccountingService.createInvoiceForJob({
        jobId: savedJob.id,
        type: "deposit",
        amount: depositAmount,
        status: "issued",
        issuedAt: nowIso,
        dueDate: nowIso.slice(0, 10),
        description: "Consultation close deposit invoice",
        notes: `Auto-generated from consultation close for quote ${quoteId}.`,
      });
      if (invoiceError || !invoice?.id) {
        console.error("[AppointmentFlow] Failed to auto-create deposit invoice:", invoiceError);
        toast.error("Job created, but deposit invoice failed", {
          description: "Please create the deposit invoice from Accounting.",
        });
      } else {
        toast.success("Deposit invoice generated", {
          description: invoice.invoice_number
            ? `Invoice ${invoice.invoice_number} created.`
            : "Deposit invoice created.",
        });
      }
    }

    setJobs(prev => [...prev, savedJob]);

    const persistedJobId = savedJob.id;
    await QuoteService.update(quoteId, { linked_job_id: persistedJobId }).catch((err) => {
      console.warn("[AppointmentFlow] Failed to link quote to created job:", err);
    });
    await LeadService.update(resolvedLeadId, {
      stage: "Closed Won",
      linked_quote_id: quoteId,
      linked_job_id: persistedJobId,
    }).catch((err) => {
      console.warn("[AppointmentFlow] Failed to persist lead close-won linkage:", err);
    });

    setLeads(prev => prev.map(l => l.id !== lead.id ? l : {
      ...l,
      stage: "Closed Won" as const,
      linkedQuoteId: quoteId,
      linkedJobId: persistedJobId,
      updatedAt: nowIso.slice(0, 10),
      stageHistory: [...l.stageHistory, {
        id: crypto.randomUUID(),
        fromStage: l.stage,
        toStage: "Closed Won" as const,
        changedAt: nowIso.slice(0, 10),
        changedBy: lead.assignedRep,
      }],
    }));

    toast.success("Contract Signed — Job Created!", { description: `${lead.primaryName} — ${derivedType} • ${savedJob.jobNumber}` });
    setCloseStage("confirmed");
    setSuccessModalOpen(true);
  };

  /* ═══════════════════════════════════════════════════════
     Step Renderers — Design Consultation Experience
     ═══════════════════════════════════════════════════════ */

  const materialFilter = config.bathroomMaterials.length === 1 ? config.bathroomMaterials[0] : undefined;

  /* ── STEP 1: Arrival — Welcoming & Personal ── */
  const renderArrival = () => (
    <div className="space-y-8 animate-fade-in">
      <StepSection label="Homeowner Verification">
        <div className="grid grid-cols-2 gap-4">
          {[true, false].map(val => (
              <button key={String(val)} type="button"
              onClick={() => {
                hasUserEditedRef.current = true;
                setArrival(prev => ({ ...prev, withHomeowner: val }));
              }}
              className={`group relative p-6 rounded-2xl border-2 text-center transition-all duration-200 ${arrival.withHomeowner === val ? "border-primary bg-primary/5 shadow-md" : "border-border hover:border-muted-foreground/30 hover:shadow-sm"}`}>
              <div className={`mx-auto w-12 h-12 rounded-xl flex items-center justify-center mb-3 transition-colors ${arrival.withHomeowner === val ? "bg-primary/15" : "bg-muted/50"}`}>
                {val ? <CheckCircle className={`h-6 w-6 ${arrival.withHomeowner === val ? "text-primary" : "text-muted-foreground"}`} /> : <X className={`h-6 w-6 ${arrival.withHomeowner === val ? "text-primary" : "text-muted-foreground"}`} />}
              </div>
              <span className="text-sm font-bold block">{val ? "Yes — Ready to Begin" : "Not Available"}</span>
              <span className="text-[11px] text-muted-foreground mt-1 block">{val ? "Homeowner is present" : "Reschedule may be needed"}</span>
            </button>
          ))}
        </div>
        {arrival.withHomeowner === false && (
          <Card className="border-warning/30 bg-warning/5 animate-fade-in">
            <CardContent className="p-4 text-sm text-warning flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 shrink-0" />
              <span>Homeowner must be present. Consider rescheduling or contacting them.</span>
            </CardContent>
          </Card>
        )}
      </StepSection>

      {arrival.withHomeowner && (
        <div className="space-y-6 animate-fade-in">
          <StepSection label="Primary Contact">
            <div className="space-y-4">
              <div>
                <Label className="text-xs font-semibold text-muted-foreground mb-1.5 block">Full Name</Label>
                <Input className="h-11 text-sm rounded-xl border-border/60 focus:border-primary" value={arrival.name} onChange={e => setArrival(prev => ({ ...prev, name: e.target.value }))} placeholder="Homeowner's full name" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-semibold text-muted-foreground mb-1.5 block">Phone</Label>
                  <Input className="h-11 text-sm rounded-xl" value={arrival.phone}
                    onChange={e => setArrival(prev => ({ ...prev, phone: formatPhoneNumber(e.target.value) }))} />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-muted-foreground mb-1.5 block">Email</Label>
                  <Input className="h-11 text-sm rounded-xl" type="email" value={arrival.primaryEmail} onChange={e => setArrival(prev => ({ ...prev, primaryEmail: e.target.value }))} />
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold text-muted-foreground mb-1.5 block">Property Address</Label>
                <Input className="h-11 text-sm rounded-xl" value={arrival.address} onChange={e => setArrival(prev => ({ ...prev, address: e.target.value }))} />
              </div>
            </div>
          </StepSection>

          <StepSection label="Secondary Contact (Optional)">
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-semibold text-muted-foreground mb-1.5 block">Name</Label>
                  <Input className="h-11 text-sm rounded-xl" value={arrival.secondaryName} onChange={e => setArrival(prev => ({ ...prev, secondaryName: e.target.value }))} />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-muted-foreground mb-1.5 block">Phone</Label>
                  <Input className="h-11 text-sm rounded-xl" value={arrival.secondaryPhone}
                    onChange={e => setArrival(prev => ({ ...prev, secondaryPhone: formatPhoneNumber(e.target.value) }))} />
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold text-muted-foreground mb-1.5 block">Email</Label>
                <Input className="h-11 text-sm rounded-xl" type="email" value={arrival.secondaryEmail} onChange={e => setArrival(prev => ({ ...prev, secondaryEmail: e.target.value }))} />
              </div>
            </div>
          </StepSection>
        </div>
      )}
    </div>
  );

  /* ── STEP 2: Discovery — Conversational & Visual ── */
  const renderDiscovery = () => {
    const painPoints = PAIN_POINTS_BY_VERTICAL[vertical] || PAIN_POINTS_BY_VERTICAL.bathroom;
    return (
      <div className="space-y-8 animate-fade-in">
        <StepSection label="What type of project are you interested in?">
          <div className="grid grid-cols-2 gap-3">
            {CONSULTATION_CATEGORIES.map(cat => {
              const Icon = cat.icon;
              const selected = discovery.category === cat.id;
              return (
                <button key={cat.id} type="button"
                  onClick={() => setDiscovery(prev => ({ ...prev, category: cat.id, painPoints: [] }))}
                  className={`group relative flex flex-col items-center gap-3 p-5 rounded-2xl border-2 text-center transition-all duration-200 ${selected ? "border-primary bg-primary/5 shadow-md" : "border-border hover:border-muted-foreground/30 hover:shadow-sm"}`}>
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-colors ${selected ? "bg-primary/15" : "bg-muted/50 group-hover:bg-muted"}`}>
                    <Icon className={`h-6 w-6 ${selected ? "text-primary" : "text-muted-foreground"}`} />
                  </div>
                  <div>
                    <span className="text-sm font-bold block">{cat.label}</span>
                    <span className="text-[10px] text-muted-foreground mt-0.5 block leading-tight">{cat.description}</span>
                  </div>
                  {selected && <div className="absolute top-2.5 right-2.5"><CheckCircle className="h-5 w-5 text-primary" /></div>}
                </button>
              );
            })}
          </div>
        </StepSection>

        {discovery.category && (
          <StepSection label="What would you most like to improve?">
            <div className="grid grid-cols-2 gap-2.5">
              {painPoints.map(pp => {
                const selected = discovery.painPoints.includes(pp.label);
                const Icon = pp.icon;
                return (
                  <button key={pp.label} type="button"
                    onClick={() => setDiscovery(prev => ({
                      ...prev,
                      painPoints: prev.painPoints.includes(pp.label)
                        ? prev.painPoints.filter(p => p !== pp.label)
                        : [...prev.painPoints, pp.label],
                    }))}
                    className={`flex items-center gap-3 p-3.5 rounded-xl border-2 text-left transition-all duration-200 ${selected ? "border-primary bg-primary/5" : "border-border hover:border-muted-foreground/30"}`}>
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-colors ${selected ? "bg-primary/15" : "bg-muted/50"}`}>
                      <Icon className={`h-4 w-4 ${selected ? "text-primary" : "text-muted-foreground"}`} />
                    </div>
                    <span className="text-xs font-semibold">{pp.label}</span>
                  </button>
                );
              })}
            </div>
          </StepSection>
        )}

        <StepSection label="Anything else we should know?">
          <Textarea className="text-sm min-h-[80px] rounded-xl border-border/60 resize-none" placeholder="Describe any specific goals, concerns, or preferences…"
            value={discovery.notes} onChange={e => setDiscovery(prev => ({ ...prev, notes: e.target.value }))} />
        </StepSection>

        <StepSection label="Permit Requirement">
          <div className="grid grid-cols-2 gap-4">
            <button
              type="button"
              onClick={() => setPermitRequired(true)}
              className={`p-5 rounded-2xl border-2 text-center transition-all duration-200 ${permitRequired ? "border-primary bg-primary/5 shadow-sm" : "border-border hover:border-muted-foreground/30"}`}
            >
              <span className="text-sm font-bold">Permit Required</span>
            </button>
            <button
              type="button"
              onClick={() => setPermitRequired(false)}
              className={`p-5 rounded-2xl border-2 text-center transition-all duration-200 ${!permitRequired ? "border-primary bg-primary/5 shadow-sm" : "border-border hover:border-muted-foreground/30"}`}
            >
              <span className="text-sm font-bold">No Permit Needed</span>
            </button>
          </div>
          <p className="text-xs text-muted-foreground">
            Jobs marked as permit-required will appear in the Permitting Portal after close.
          </p>
        </StepSection>
      </div>
    );
  };

  /* ── STEP 3: Site (Present State) — Visual Layout Selection ── */
  const renderSite = () => {
    if (vertical === "windows") {
      return (
        <div className="space-y-8 animate-fade-in">
          <StepSection label="Number of Windows">
            <Input type="number" className="h-11 text-sm w-28 rounded-xl" min={1} value={config.windowCount || ""} onChange={e => update("windowCount", parseInt(e.target.value) || 0)} />
          </StepSection>
          <StepSection label="Window Types">
            <div className="grid grid-cols-2 gap-2.5">
              {WINDOW_TYPES.map(wt => {
                const selected = config.windowTypes.includes(wt);
                return (
                  <button key={wt} type="button" onClick={() => {
                    setConfig(prev => ({
                      ...prev,
                      windowTypes: prev.windowTypes.includes(wt) ? prev.windowTypes.filter(x => x !== wt) : [...prev.windowTypes, wt],
                    }));
                  }}
                    className={`flex items-center gap-3 p-3.5 rounded-xl border-2 text-left transition-all duration-200 ${selected ? "border-primary bg-primary/5" : "border-border hover:border-muted-foreground/30"}`}>
                    <SelectionIndicator checked={selected} />
                    <span className="text-xs font-semibold">{wt}</span>
                  </button>
                );
              })}
            </div>
          </StepSection>
        </div>
      );
    }

    if (vertical === "doors") {
      return (
        <div className="space-y-8 animate-fade-in">
          <StepSection label="Entry Type">
            <div className="grid grid-cols-2 gap-2.5">
              {DOOR_ENTRY_TYPES.map(dt => (
                <button key={dt} type="button" onClick={() => update("doorEntryType", dt)}
                  className={`p-4 rounded-xl border-2 text-center transition-all duration-200 ${config.doorEntryType === dt ? "border-primary bg-primary/5 shadow-sm" : "border-border hover:border-muted-foreground/30"}`}>
                  <span className="text-sm font-semibold">{dt}</span>
                </button>
              ))}
            </div>
          </StepSection>
          <StepSection label="Door Size">
            <div className="grid grid-cols-3 gap-2.5">
              {DOOR_SIZES.map(ds => (
                <button key={ds} type="button" onClick={() => update("doorSize", ds)}
                  className={`p-4 rounded-xl border-2 text-center transition-all duration-200 ${config.doorSize === ds ? "border-primary bg-primary/5 shadow-sm" : "border-border hover:border-muted-foreground/30"}`}>
                  <span className="text-sm font-bold">{ds}</span>
                </button>
              ))}
            </div>
          </StepSection>
        </div>
      );
    }

    // Bathroom — Present State
    return (
      <div className="space-y-8 animate-fade-in">
        <StepSection label="What's the layout of the space?">
          <div className="space-y-3">
            {LAYOUT_OPTIONS.map(lo => {
              const Icon = lo.icon;
              const selected = config.layoutType === lo.id;
              return (
                <button key={lo.id} type="button"
                  onClick={() => setConfig(prev => ({ ...prev, layoutType: lo.id, wallCount: lo.wallCount || prev.wallCount }))}
                  className={`w-full flex items-center gap-4 p-5 rounded-2xl border-2 text-left transition-all duration-200 ${selected ? "border-primary bg-primary/5 shadow-md" : "border-border hover:border-muted-foreground/30 hover:shadow-sm"}`}>
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 transition-colors ${selected ? "bg-primary/15" : "bg-muted/50"}`}>
                    <Icon className={`h-6 w-6 ${selected ? "text-primary" : "text-muted-foreground"}`} />
                  </div>
                  <div className="flex-1">
                    <span className="text-sm font-bold block">{lo.label}</span>
                    <span className="text-xs text-muted-foreground mt-0.5 block">{lo.description}</span>
                  </div>
                  <div className={`h-6 w-6 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${selected ? "border-primary bg-primary" : "border-muted-foreground/30"}`}>
                    {selected && <Check className="h-3.5 w-3.5 text-primary-foreground" />}
                  </div>
                </button>
              );
            })}
          </div>
        </StepSection>

        {config.layoutType === "Custom" && (
          <StepSection label="How many walls?">
            <div className="grid grid-cols-4 gap-3">
              {[1, 2, 3, 4].map(n => (
                <button key={n} type="button" onClick={() => update("wallCount", n)}
                  className={`p-4 rounded-xl border-2 text-center transition-all duration-200 ${config.wallCount === n ? "border-primary bg-primary/5 shadow-sm" : "border-border hover:border-muted-foreground/30"}`}>
                  <span className="text-lg font-bold">{n}</span>
                </button>
              ))}
            </div>
          </StepSection>
        )}

        <StepSection label="Is there a window in the shower space?">
          <div className="grid grid-cols-2 gap-4">
            {[true, false].map(val => (
              <button key={String(val)} type="button" onClick={() => update("hasWindow", val)}
                className={`p-5 rounded-2xl border-2 text-center transition-all duration-200 ${config.hasWindow === val ? "border-primary bg-primary/5 shadow-sm" : "border-border hover:border-muted-foreground/30"}`}>
                <span className="text-sm font-bold">{val ? "Yes" : "No"}</span>
              </button>
            ))}
          </div>
        </StepSection>

        <StepSection label="Any obstructions or built-in features?">
          <div className="grid grid-cols-2 gap-2.5">
            {OBSTRUCTION_OPTIONS.map(obs => {
              const isNone = obs === "None / Not Applicable";
              const selected = config.obstructions.includes(obs);
              return (
                <button key={obs} type="button" onClick={() => {
                  if (isNone) {
                    setConfig(prev => ({ ...prev, obstructions: prev.obstructions.includes(obs) ? [] : [obs] }));
                  } else {
                    setConfig(prev => {
                      const without = prev.obstructions.filter(o => o !== "None / Not Applicable");
                      return { ...prev, obstructions: without.includes(obs) ? without.filter(o => o !== obs) : [...without, obs] };
                    });
                  }
                }}
                  className={`flex items-center gap-3 p-3.5 rounded-xl border-2 text-left transition-all duration-200 ${selected ? "border-primary bg-primary/5" : "border-border hover:border-muted-foreground/30"}`}>
                  <SelectionIndicator checked={selected} />
                  <span className="text-xs font-semibold">{obs}</span>
                </button>
              );
            })}
          </div>
        </StepSection>
      </div>
    );
  };

  /* ── STEP 4: Measurements ── */
  const renderMeasure = () => {
    if (!isBathroom) {
      return (
        <div className="flex flex-col items-center justify-center py-16 text-center animate-fade-in">
          <div className="w-16 h-16 rounded-2xl bg-muted/50 flex items-center justify-center mb-4">
            <Ruler className="h-8 w-8 text-muted-foreground/40" />
          </div>
          <p className="text-base font-bold text-muted-foreground">Measurements</p>
          <p className="text-sm text-muted-foreground/70 mt-2 max-w-xs">Detailed measurements will be captured during site survey for {vertical} projects.</p>
        </div>
      );
    }

    const wallLabels = ["Left Wall", "Back Wall (Soap Dish Wall)", "Right Wall (Wing Wall)"];
    const wallKeys: ("wallA" | "wallB" | "wallC")[] = ["wallA", "wallB", "wallC"];
    const visibleWalls = wallKeys.slice(0, Math.min(config.wallCount, 3));
    const showQuickSelect = config.layoutType === "Alcove";

    return (
      <div className="space-y-8 animate-fade-in">
        {showQuickSelect && (
          <StepSection label="Standard Size Quick Select">
            <div className="grid grid-cols-4 gap-3">
              {STANDARD_SIZE_PRESETS.map(preset => (
                <button key={preset.label} type="button" onClick={() => applySizePreset(preset)}
                  className={`p-4 rounded-xl border-2 text-center transition-all duration-200 ${selectedSizePreset === preset.label ? "border-primary bg-primary/5 shadow-md" : "border-border hover:border-muted-foreground/30 hover:shadow-sm"}`}>
                  <span className="text-base font-bold">{preset.label}</span>
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">Select a standard size to auto-fill, or enter manually below.</p>
          </StepSection>
        )}

        <StepSection label="Base Footprint">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-[11px] text-muted-foreground mb-1 block">Width (inches)</Label>
              <Input type="number" className="h-11 text-sm rounded-xl" placeholder="e.g. 30" value={config.baseWidth} onChange={e => { update("baseWidth", e.target.value); setSelectedSizePreset(""); }} />
            </div>
            <div>
              <Label className="text-[11px] text-muted-foreground mb-1 block">Depth (inches)</Label>
              <Input type="number" className="h-11 text-sm rounded-xl" placeholder="e.g. 60" value={config.baseLength} onChange={e => { update("baseLength", e.target.value); setSelectedSizePreset(""); }} />
            </div>
          </div>
        </StepSection>

        {visibleWalls.map((wk, i) => {
          const wall = config.wallMeasurements[wk];
          return (
            <StepSection key={wk} label={wallLabels[i]}>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[11px] text-muted-foreground mb-1 block">Width (inches)</Label>
                  <Input type="number" className="h-11 text-sm rounded-xl" value={wall.width} onChange={e => { updateWall(wk, "width", e.target.value); setSelectedSizePreset(""); }} />
                </div>
                <div>
                  <Label className="text-[11px] text-muted-foreground mb-1 block">Height (inches)</Label>
                  <Input type="number" className="h-11 text-sm rounded-xl" value={wall.height} onChange={e => { updateWall(wk, "height", e.target.value); setSelectedSizePreset(""); }} />
                </div>
              </div>
            </StepSection>
          );
        })}

        <StepSection label="Where is the drain located?">
          <div className="grid grid-cols-4 gap-3">
            {["Left", "Center", "Right", "Custom"].map(loc => (
              <button key={loc} type="button" onClick={() => update("drainLocation", loc)}
                className={`p-4 rounded-xl border-2 text-center transition-all duration-200 ${config.drainLocation === loc ? "border-primary bg-primary/5 shadow-sm" : "border-border hover:border-muted-foreground/30"}`}>
                <span className="text-sm font-semibold">{loc}</span>
              </button>
            ))}
          </div>
        </StepSection>
      </div>
    );
  };

  /* ── STEP 5: Structural — Clean & Simple ── */
  const renderStructure = () => {
    if (!isBathroom) {
      return (
        <div className="flex flex-col items-center justify-center py-16 text-center animate-fade-in">
          <div className="w-16 h-16 rounded-2xl bg-muted/50 flex items-center justify-center mb-4">
            <Hammer className="h-8 w-8 text-muted-foreground/40" />
          </div>
          <p className="text-base font-bold text-muted-foreground">Modifications</p>
          <p className="text-sm text-muted-foreground/70 mt-2 max-w-xs">No structural modifications required for {vertical} projects.</p>
        </div>
      );
    }

    const structuralOptions: { key: keyof StructuralChanges; label: string; description: string }[] = [
      { key: "removingWalls", label: "Removing Walls", description: "Taking down existing walls" },
      { key: "addingWalls", label: "Adding / Building Walls", description: "Constructing new walls" },
      { key: "movingDrain", label: "Moving Drain", description: "Relocating the drain position" },
      { key: "movingPlumbing", label: "Moving Plumbing", description: "Relocating supply lines" },
      { key: "subfloorMod", label: "Subfloor Modification", description: "Repairing or replacing subfloor" },
    ];

    const hasAnyStructural = structuralOptions.some(o => config.structuralChanges[o.key] === true);

    return (
      <div className="space-y-8 animate-fade-in">
        <StepSection label="Select all that apply">
          <div className="space-y-2.5">
            <button type="button" onClick={() => {
              if (hasAnyStructural) {
                setConfig(prev => ({
                  ...prev,
                  structuralChanges: { ...prev.structuralChanges, removingWalls: false, addingWalls: false, movingDrain: false, movingPlumbing: false, subfloorMod: false },
                }));
              }
            }}
              className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 text-left transition-all duration-200 ${!hasAnyStructural ? "border-primary bg-primary/5 shadow-sm" : "border-border hover:border-muted-foreground/30"}`}>
              <SelectionIndicator checked={!hasAnyStructural} />
              <div>
                <span className="text-sm font-bold block">No Structural Changes</span>
                <span className="text-[11px] text-muted-foreground">The space stays as-is</span>
              </div>
            </button>

            {structuralOptions.map(t => {
              const val = config.structuralChanges[t.key] as boolean;
              return (
                <button key={t.key} type="button" onClick={() => updateStructural(t.key, !val)}
                  className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 text-left transition-all duration-200 ${val ? "border-primary bg-primary/5 shadow-sm" : "border-border hover:border-muted-foreground/30"}`}>
                  <SelectionIndicator checked={val} />
                  <div>
                    <span className="text-sm font-bold block">{t.label}</span>
                    <span className="text-[11px] text-muted-foreground">{t.description}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </StepSection>

        {config.hasWindow && (
          <StepSection label="Modify the existing window?">
            <div className="grid grid-cols-2 gap-4">
              {(["Yes", "No"] as const).map(val => (
                <button key={val} type="button" onClick={() => updateStructural("windowMod", val)}
                  className={`p-5 rounded-2xl border-2 text-center transition-all duration-200 ${config.structuralChanges.windowMod === val ? "border-primary bg-primary/5 shadow-sm" : "border-border hover:border-muted-foreground/30"}`}>
                  <span className="text-sm font-bold">{val}</span>
                </button>
              ))}
            </div>
          </StepSection>
        )}
      </div>
    );
  };

  /* ── STEP 6: Design — Interactive Visual Builder ── */
  const renderDesign = () => {
    if (!isBathroom) {
      return (
        <div className="flex flex-col items-center justify-center py-16 text-center animate-fade-in">
          <div className="w-16 h-16 rounded-2xl bg-muted/50 flex items-center justify-center mb-4">
            <Paintbrush className="h-8 w-8 text-muted-foreground/40" />
          </div>
          <p className="text-base font-bold text-muted-foreground">Design Selections</p>
          <p className="text-sm text-muted-foreground/70 mt-2 max-w-xs">{vertical === "windows" ? "Window" : "Door"} design options will be configured during the order phase.</p>
        </div>
      );
    }

    const catalogWallSystems = getProductsByCategory("wall_system");
    const systemOptions = isWalkInTub
      ? catalogWallSystems.filter(p => p.materialFilter === "Ellas Bubbles" || p.materialFilter === "Custom WIT")
      : catalogWallSystems.filter(p => p.materialFilter !== "Ellas Bubbles" && p.materialFilter !== "Custom WIT");

    const selectedWallProduct = catalogWallSystems.find(p => p.materialFilter === wallSystemId);
    const wallColorVariants = selectedWallProduct ? getActiveVariants(selectedWallProduct.id) : [];
    const catalogBases = getProductsByCategory("base", wallSystemId);
    const catalogFixtures = getProductsByCategory("fixture");
    const catalogGrabBars = getProductsByCategory("grab_bar");
    const catalogAddOns = getProductsByCategory("add_on");
    const isCustomGlass = config.enclosureType === "Semi-Frameless Glass";

    return (
      <div className="space-y-10 animate-fade-in">
        {/* 1. System Selection */}
        <StepSection label="1 · Wall System">
          <div className="space-y-2.5">
            {systemOptions.map(p => {
              const matId = p.materialFilter || p.displayName;
              const selected = config.bathroomMaterials.includes(matId as BathroomMaterial);
              return (
                <button key={p.id} type="button" onClick={() => setWallSystem(matId)}
                  className={`w-full text-left p-4 rounded-2xl border-2 transition-all duration-200 ${selected ? "border-primary bg-primary/5 shadow-md" : "border-border hover:border-muted-foreground/30 hover:shadow-sm"}`}>
                  <div className="flex items-center gap-4">
                    <div className={`h-6 w-6 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${selected ? "border-primary bg-primary" : "border-muted-foreground/30"}`}>
                      {selected && <Check className="h-3.5 w-3.5 text-primary-foreground" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold">{p.displayName}</span>
                        {p.vendor && <Badge variant="outline" className="text-[9px] h-4">{p.vendor}</Badge>}
                      </div>
                      {p.notes && <p className="text-xs text-muted-foreground mt-0.5">{p.notes}</p>}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </StepSection>

        {/* 2. Wall Color — Visual Swatches */}
        {wallColorVariants.length > 0 && (
          <StepSection label="2 · Choose Your Color">
            <div className="grid grid-cols-4 gap-3">
              {wallColorVariants.map((v, idx) => {
                const selected = config.wallColor === v.name;
                const hue = (idx * 37 + v.name.charCodeAt(0) * 7) % 360;
                const sat = 15 + (idx % 4) * 10;
                const light = 60 + (idx % 5) * 6;
                return (
                  <button key={v.id} type="button" onClick={() => update("wallColor", v.name)}
                    className={`group flex flex-col items-center gap-2 p-3 rounded-xl border-2 text-center transition-all duration-200 ${selected ? "border-primary bg-primary/5 shadow-md" : "border-border hover:border-muted-foreground/30 hover:shadow-sm"}`}>
                    <div className="w-full aspect-square rounded-lg border border-border/50 overflow-hidden">
                      {v.imageUrl ? (
                        <img src={v.imageUrl} alt={v.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full" style={{ background: `hsl(${hue} ${sat}% ${light}%)` }} />
                      )}
                    </div>
                    <span className="text-[10px] font-semibold leading-tight">{v.name}</span>
                  </button>
                );
              })}
            </div>
          </StepSection>
        )}

        {/* 3. Base Type */}
        <StepSection label="3 · Base Type">
          <div className="flex items-center justify-between mb-1">
            <span />
            {catalogBases.some(bt => bt.pricingImpact !== "Included") && (
              <button type="button" className="inline-flex items-center gap-1 text-[10px] text-muted-foreground hover:text-primary transition-colors" onClick={() => setPricingDeltaVisible(prev => ({ ...prev, section_base: !prev.section_base }))}>
                <Binoculars className="h-3.5 w-3.5" />
                {pricingDeltaVisible.section_base ? "Hide" : "View"} upgrades
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            {catalogBases.map(bt => (
              <button key={bt.id} type="button" onClick={() => update("baseType", bt.displayName)}
                className={`p-4 rounded-xl border-2 text-center transition-all duration-200 ${config.baseType === bt.displayName ? "border-primary bg-primary/5 shadow-sm" : "border-border hover:border-muted-foreground/30"}`}>
                <span className="text-sm font-semibold">{bt.displayName}</span>
                {pricingDeltaVisible.section_base && bt.pricingImpact !== "Included" && (
                  <span className="text-[10px] text-primary block mt-1">{bt.pricingImpact}</span>
                )}
              </button>
            ))}
          </div>
        </StepSection>

        {/* 4. Hardware Finish — Visual Swatches */}
        <StepSection label="4 · Hardware Finish">
          <div className="grid grid-cols-3 gap-3">
            {HARDWARE_FINISHES.map(hf => {
              const selected = config.hardwareFinish === hf.id;
              return (
                <button key={hf.id} type="button" onClick={() => update("hardwareFinish", hf.id)}
                  className={`group flex flex-col items-center gap-2.5 p-4 rounded-xl border-2 text-center transition-all duration-200 ${selected ? "border-primary bg-primary/5 shadow-md" : "border-border hover:border-muted-foreground/30 hover:shadow-sm"}`}>
                  <div className={`h-10 w-10 rounded-full ${hf.swatch} border border-border shadow-inner`} />
                  <span className="text-[11px] font-bold">{hf.label}</span>
                </button>
              );
            })}
          </div>
          {config.hardwareFinish === "Multi" && (
            <p className="text-xs text-muted-foreground flex items-center gap-1.5 mt-2">
              <Info className="h-3.5 w-3.5" /> Per-item hardware color selection available during Build Review.
            </p>
          )}
        </StepSection>

        {/* 5. Enclosure Type */}
        <StepSection label="5 · Enclosure Type">
          <div className="space-y-2.5">
            {ENCLOSURE_OPTIONS.map(enc => {
              const selected = config.enclosureType === enc.id;
              return (
                <button key={enc.id} type="button" onClick={() => update("enclosureType", enc.id as EnclosureType)}
                  className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 text-left transition-all duration-200 ${selected ? "border-primary bg-primary/5 shadow-md" : "border-border hover:border-muted-foreground/30 hover:shadow-sm"}`}>
                  <div className={`h-6 w-6 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${selected ? "border-primary bg-primary" : "border-muted-foreground/30"}`}>
                    {selected && <Check className="h-3.5 w-3.5 text-primary-foreground" />}
                  </div>
                  <div>
                    <span className="text-sm font-bold block">{enc.label}</span>
                    <span className="text-[11px] text-muted-foreground">{enc.description}</span>
                  </div>
                </button>
              );
            })}
          </div>
          {isCustomGlass && (
            <Card className="mt-3 border-warning/30 bg-warning/5">
              <CardContent className="p-3 text-xs flex items-center gap-2">
                <Info className="h-4 w-4 shrink-0 text-warning" />
                <span>Custom glass may add up to 1 additional week to install.</span>
              </CardContent>
            </Card>
          )}
        </StepSection>

        {/* 6. Fixture Package */}
        <StepSection label="6 · Fixture Package">
          <div className="flex items-center justify-between mb-1">
            <span />
            {catalogFixtures.some(fo => fo.pricingImpact !== "Included") && (
              <button type="button" className="inline-flex items-center gap-1 text-[10px] text-muted-foreground hover:text-primary transition-colors" onClick={() => setPricingDeltaVisible(prev => ({ ...prev, section_fixture: !prev.section_fixture }))}>
                <Binoculars className="h-3.5 w-3.5" />
                {pricingDeltaVisible.section_fixture ? "Hide" : "View"} upgrades
              </button>
            )}
          </div>
          <div className="space-y-2">
            {[...catalogFixtures].sort((a, b) => {
              const order = ["standard", "handheld", "sliding bar", "rainfall"];
              const aIdx = order.findIndex(o => a.displayName.toLowerCase().includes(o));
              const bIdx = order.findIndex(o => b.displayName.toLowerCase().includes(o));
              return (aIdx === -1 ? 99 : aIdx) - (bIdx === -1 ? 99 : bIdx);
            }).map(fo => {
              const selected = config.selectedFixture === fo.id;
              return (
                <button key={fo.id} type="button" onClick={() => update("selectedFixture", fo.id)}
                  className={`w-full flex items-center gap-3 p-3.5 rounded-xl border-2 text-left transition-all duration-200 ${selected ? "border-primary bg-primary/5 shadow-sm" : "border-border hover:border-muted-foreground/30"}`}>
                  <div className={`h-5 w-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${selected ? "border-primary bg-primary/40" : "border-muted-foreground/30"}`}>
                    {selected && <Check className="h-3 w-3 text-primary-foreground" />}
                  </div>
                  <span className="text-sm font-medium flex-1">{fo.displayName}</span>
                  {pricingDeltaVisible.section_fixture && fo.pricingImpact !== "Included" && (
                    <span className="text-[10px] text-primary">{fo.pricingImpact}</span>
                  )}
                  {config.hardwareFinish && config.hardwareFinish !== "Multi" && selected && (
                    <Badge variant="outline" className="text-[9px] h-5">{config.hardwareFinish}</Badge>
                  )}
                </button>
              );
            })}
          </div>
        </StepSection>

        {/* 7. Grab Bars */}
        <StepSection label="7 · Safety & Grab Bars">
          <div className="flex items-center justify-between mb-1">
            <span />
            {catalogGrabBars.some(gb => gb.pricingImpact !== "Included") && (
              <button type="button" className="inline-flex items-center gap-1 text-[10px] text-muted-foreground hover:text-primary transition-colors" onClick={() => setPricingDeltaVisible(prev => ({ ...prev, section_grabbars: !prev.section_grabbars }))}>
                <Binoculars className="h-3.5 w-3.5" />
                {pricingDeltaVisible.section_grabbars ? "Hide" : "View"} upgrades
              </button>
            )}
          </div>
          <div className="space-y-2">
            {catalogGrabBars.map(gb => {
              const selected = config.selectedGrabBars.includes(gb.id);
              return (
                <button key={gb.id} type="button" onClick={() => toggleArray("selectedGrabBars", gb.id)}
                  className={`w-full flex items-center gap-3 p-3.5 rounded-xl border-2 text-left transition-all duration-200 ${selected ? "border-primary bg-primary/5" : "border-border hover:border-muted-foreground/30"}`}>
                  <SelectionIndicator checked={selected} />
                  <span className="text-sm font-medium flex-1">{gb.displayName}</span>
                  {pricingDeltaVisible.section_grabbars && gb.pricingImpact !== "Included" && (
                    <span className="text-[10px] text-primary">{gb.pricingImpact}</span>
                  )}
                  {config.hardwareFinish && config.hardwareFinish !== "Multi" && selected && (
                    <Badge variant="outline" className="text-[9px] h-5">{config.hardwareFinish}</Badge>
                  )}
                </button>
              );
            })}
          </div>
        </StepSection>

        {/* ═══ Full Bathroom Remodel: Additional Selections ═══ */}
        {discovery.category === "Full Bathroom Remodel" && (
          <>
            <StepSection label="8 · Flooring">
              <div className="grid grid-cols-2 gap-3">
                {["Luxury Vinyl Plank", "Ceramic Tile", "Porcelain Tile", "Natural Stone", "Waterproof Laminate"].map(opt => {
                  const selected = config.fullBathroomSelections?.flooring === opt;
                  return (
                    <button key={opt} type="button" onClick={() => setConfig(prev => ({ ...prev, fullBathroomSelections: { ...prev.fullBathroomSelections, flooring: opt } }))}
                      className={`p-4 rounded-xl border-2 text-center transition-all duration-200 ${selected ? "border-primary bg-primary/5 shadow-sm" : "border-border hover:border-muted-foreground/30"}`}>
                      <span className="text-sm font-semibold">{opt}</span>
                    </button>
                  );
                })}
              </div>
            </StepSection>

            <StepSection label="9 · Toilet">
              <div className="grid grid-cols-2 gap-3">
                {["Standard Replacement", "Comfort Height", "Elongated Bowl", "ADA Compliant", "Keep Existing"].map(opt => {
                  const selected = config.fullBathroomSelections?.toilet === opt;
                  return (
                    <button key={opt} type="button" onClick={() => setConfig(prev => ({ ...prev, fullBathroomSelections: { ...prev.fullBathroomSelections, toilet: opt } }))}
                      className={`p-4 rounded-xl border-2 text-center transition-all duration-200 ${selected ? "border-primary bg-primary/5 shadow-sm" : "border-border hover:border-muted-foreground/30"}`}>
                      <span className="text-sm font-semibold">{opt}</span>
                    </button>
                  );
                })}
              </div>
            </StepSection>

            <StepSection label="10 · Vanity">
              <div className="grid grid-cols-2 gap-3">
                {["Single Sink Vanity", "Double Sink Vanity", "Floating Vanity", "Pedestal Sink", "Keep Existing"].map(opt => {
                  const selected = config.fullBathroomSelections?.vanity === opt;
                  return (
                    <button key={opt} type="button" onClick={() => setConfig(prev => ({ ...prev, fullBathroomSelections: { ...prev.fullBathroomSelections, vanity: opt } }))}
                      className={`p-4 rounded-xl border-2 text-center transition-all duration-200 ${selected ? "border-primary bg-primary/5 shadow-sm" : "border-border hover:border-muted-foreground/30"}`}>
                      <span className="text-sm font-semibold">{opt}</span>
                    </button>
                  );
                })}
              </div>
            </StepSection>
          </>
        )}

        {/* Add-ons — numbered dynamically based on whether FBR sections are shown */}
        {(() => {
          const addOnStepNum = discovery.category === "Full Bathroom Remodel" ? 11 : 8;
          const FULL_BATH_ADDONS = ["Paint", "Lighting", "Drywall Work", "Additional Trim"];

          return (
            <StepSection label={`${addOnStepNum} · Finishing Touches`}>
              <div className="flex items-center justify-between mb-1">
                <span />
                {catalogAddOns.some(ao => ao.pricingImpact !== "Included") && (
                  <button type="button" className="inline-flex items-center gap-1 text-[10px] text-muted-foreground hover:text-primary transition-colors" onClick={() => setPricingDeltaVisible(prev => ({ ...prev, section_addons: !prev.section_addons }))}>
                    <Binoculars className="h-3.5 w-3.5" />
                    {pricingDeltaVisible.section_addons ? "Hide" : "View"} upgrades
                  </button>
                )}
              </div>

              <button type="button" onClick={() => {
                const next = !noAddOns;
                setNoAddOns(next);
                if (next) setConfig(prev => ({ ...prev, selectedAddOns: [], customAddOns: [], noAddOnsSelected: true, fullBathroomAddOns: [], fullBathroomCustomAddOns: [] }));
                else setConfig(prev => ({ ...prev, noAddOnsSelected: false }));
              }}
                className={`w-full flex items-center gap-3 p-4 rounded-xl border-2 text-left transition-all duration-200 mb-3 ${noAddOns ? "border-primary bg-primary/5 shadow-sm" : "border-border hover:border-muted-foreground/30"}`}>
                <SelectionIndicator checked={noAddOns} />
                <span className="text-sm font-bold">No Additional Add-Ons</span>
              </button>

              <div className={`space-y-2 ${noAddOns ? "opacity-40 pointer-events-none" : ""}`}>
                {/* Standard catalog add-ons */}
                {catalogAddOns.map(ao => {
                  const selected = config.selectedAddOns.includes(ao.displayName);
                  return (
                    <button key={ao.id} type="button" onClick={() => {
                      if (noAddOns) return;
                      setNoAddOns(false);
                      toggleArray("selectedAddOns", ao.displayName);
                    }}
                      className={`w-full flex items-center gap-3 p-3.5 rounded-xl border-2 text-left transition-all duration-200 ${selected ? "border-primary bg-primary/5" : "border-border hover:border-muted-foreground/30"}`}>
                      <SelectionIndicator checked={selected} />
                      <span className="text-sm font-medium flex-1">{ao.displayName}</span>
                      {pricingDeltaVisible.section_addons && ao.pricingImpact !== "Included" && (
                        <span className="text-[10px] text-primary">{ao.pricingImpact}</span>
                      )}
                    </button>
                  );
                })}

                {/* Full Bathroom Remodel specific add-ons */}
                {discovery.category === "Full Bathroom Remodel" && FULL_BATH_ADDONS.map(addon => {
                  const selected = (config.fullBathroomAddOns || []).includes(addon);
                  return (
                    <button key={addon} type="button" onClick={() => {
                      if (noAddOns) return;
                      setNoAddOns(false);
                      setConfig(prev => {
                        const arr = prev.fullBathroomAddOns || [];
                        return { ...prev, fullBathroomAddOns: arr.includes(addon) ? arr.filter(a => a !== addon) : [...arr, addon] };
                      });
                    }}
                      className={`w-full flex items-center gap-3 p-3.5 rounded-xl border-2 text-left transition-all duration-200 ${selected ? "border-primary bg-primary/5" : "border-border hover:border-muted-foreground/30"}`}>
                      <SelectionIndicator checked={selected} />
                      <span className="text-sm font-medium flex-1">{addon}</span>
                      <Badge variant="outline" className="text-[9px] h-4">Full Remodel</Badge>
                    </button>
                  );
                })}
              </div>

              {/* Custom add-ons / "Other" items */}
              <div className={`mt-4 space-y-2 ${noAddOns ? "opacity-40 pointer-events-none" : ""}`}>
                {(config.customAddOns || []).map(ca => (
                  <div key={ca.id} className="flex items-center gap-2 p-3 rounded-xl border border-border bg-muted/20">
                    <Input className="h-9 text-sm flex-1 rounded-lg" placeholder="Custom item name" value={ca.name}
                      onChange={e => updateCustomAddOn(ca.id, "name", e.target.value)} />
                    <Input className="h-9 text-sm w-24 rounded-lg" placeholder="$ Price" type="number" value={ca.priceOverride}
                      onChange={e => updateCustomAddOn(ca.id, "priceOverride", e.target.value)} />
                    <Button variant="ghost" size="sm" className="h-9 w-9 p-0 text-destructive" onClick={() => removeCustomAddOn(ca.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" className="h-9 text-xs gap-1.5 w-full rounded-xl" onClick={() => { setNoAddOns(false); addCustomAddOn(); }}>
                  <Plus className="h-3.5 w-3.5" /> {discovery.category === "Full Bathroom Remodel" ? "Add Other Item" : "Add Custom Item"}
                </Button>
              </div>
            </StepSection>
          );
        })()}

        {/* Real-time Build Summary */}
        <DesignBuildSummary config={config} isBathroom={isBathroom} />
      </div>
    );
  };

  /* ── STEP 7: Discounts — Warm & Appreciative ── */
  const DISCOUNT_MESSAGES: Record<string, string> = {
    military: "Thank you for your service. We appreciate you and your family. Military discount applied.",
    senior: "We appreciate you. Senior discount applied.",
    frontliner: "Thank you for your service and dedication. Frontliner discount applied.",
  };

  const renderDiscounts = () => {
    const discountOptions: { key: keyof typeof config.discounts; label: string; icon: React.ElementType }[] = [
      { key: "military", label: "Military Discount", icon: Shield },
      { key: "senior", label: "Senior Discount", icon: Heart },
      { key: "frontliner", label: "Frontliner Discount", icon: Star },
    ];

    const activeDiscounts = discountOptions.filter(d => config.discounts?.[d.key]);

    return (
      <div className="space-y-8 animate-fade-in">
        <StepSection label="Do any of these apply?">
          <div className="space-y-3">
            {discountOptions.map(d => {
              const selected = config.discounts?.[d.key] || false;
              const Icon = d.icon;
              return (
                <button key={d.key} type="button" onClick={() => {
                  setConfig(prev => ({
                    ...prev,
                    discounts: { ...prev.discounts, [d.key]: !selected },
                  }));
                }}
                  className={`w-full flex items-center gap-4 p-5 rounded-2xl border-2 text-left transition-all duration-200 ${selected ? "border-primary bg-primary/5 shadow-md" : "border-border hover:border-muted-foreground/30 hover:shadow-sm"}`}>
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 transition-colors ${selected ? "bg-primary/15" : "bg-muted/50"}`}>
                    <Icon className={`h-5 w-5 ${selected ? "text-primary" : "text-muted-foreground"}`} />
                  </div>
                  <span className="text-sm font-bold flex-1">{d.label}</span>
                  {selected && <CheckCircle className="h-5 w-5 text-primary" />}
                </button>
              );
            })}
          </div>
        </StepSection>

        {activeDiscounts.length > 0 && (
          <div className="space-y-3 animate-fade-in">
            {activeDiscounts.map(d => (
              <Card key={d.key} className="border-primary/20 bg-primary/5 rounded-2xl">
                <CardContent className="p-5 flex items-start gap-4">
                  <Heart className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                  <p className="text-sm text-foreground leading-relaxed">{DISCOUNT_MESSAGES[d.key]}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  };

  /* ── Scope Gate — Add Another Scope or Continue ── */

  const handleEditScope = (scopeId: string) => {
    const scope = completedScopes.find(s => s.id === scopeId);
    if (!scope) return;
    // Load scope data back into active state
    setDiscovery({ category: scope.category, painPoints: scope.painPoints, notes: scope.notes });
    setConfig({ ...scope.config });
    setNoAddOns(scope.noAddOns);
    // Remove from completed
    setCompletedScopes(prev => prev.filter(s => s.id !== scopeId));
    setShowScopeGate(false);
    setCurrentStep(1); // Go back to Discovery
  };

  const handleConfirmDeleteScope = (scopeId: string) => {
    setCompletedScopes(prev => prev.filter(s => s.id !== scopeId));
    setScopeDeleteConfirm(null);
    // If no scopes left, go back to discovery
    if (completedScopes.length <= 1) {
      setShowScopeGate(false);
      setCurrentStep(1);
    }
  };

  const renderScopeGate = () => {
    const SCOPE_ICONS: Record<string, React.ElementType> = {
      "Shower Remodel": ShowerHead, "Bath Conversion": Bath, "Full Bathroom Remodel": Hammer,
      "Walk-in Tub": Bath, "Windows": Square, "Doors": DoorOpen,
    };
    return (
      <div className="max-w-xl mx-auto py-8 space-y-8 animate-fade-in">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4" style={{ background: brandColorMid }}>
            <CheckCircle className="h-8 w-8" style={{ color: brandColor }} />
          </div>
          <h3 className="text-2xl font-bold text-foreground mb-2">Scope Complete!</h3>
          <p className="text-sm text-muted-foreground leading-relaxed max-w-md mx-auto">
            Your {completedScopes[completedScopes.length - 1]?.category || "project"} design has been saved. Would you like to add another project scope?
          </p>
        </div>

        {/* Completed scopes — NO pricing shown */}
        <div className="space-y-3">
          <p className="text-xs font-bold text-muted-foreground uppercase tracking-[0.15em]">
            {completedScopes.length} {completedScopes.length === 1 ? "Scope" : "Scopes"} Configured
          </p>
          {completedScopes.map((s) => {
            const SIcon = SCOPE_ICONS[s.category] || Package;
            return (
              <div key={s.id} className="flex items-center gap-4 p-4 rounded-2xl border-2 border-border/50 bg-card/80 shadow-sm">
                <div className="p-2.5 rounded-xl shrink-0" style={{ background: brandColorMid }}>
                  <SIcon className="h-5 w-5" style={{ color: brandColor }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-foreground">{s.category}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {s.config.bathroomMaterials?.[0] || s.config.autoProjectType || "Configured"}
                    {s.config.baseWidth && s.config.baseLength ? ` · ${s.config.baseWidth}" × ${s.config.baseLength}"` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => handleEditScope(s.id)}
                    className="p-1.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                    title="Edit scope">
                    <Paintbrush className="h-4 w-4" />
                  </button>
                  <button onClick={() => setScopeDeleteConfirm(s.id)}
                    className="p-1.5 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                    title="Delete scope">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Delete confirmation */}
        {scopeDeleteConfirm && (
          <Card className="border-destructive/30 bg-destructive/5 rounded-2xl animate-fade-in">
            <CardContent className="p-5">
              <p className="text-sm font-bold text-foreground mb-2">Remove this scope?</p>
              <p className="text-xs text-muted-foreground mb-4">
                {completedScopes.length === 1
                  ? "This is your only scope. Removing it will take you back to the beginning."
                  : "This action cannot be undone."}
              </p>
              <div className="flex gap-2">
                <Button size="sm" variant="destructive" onClick={() => handleConfirmDeleteScope(scopeDeleteConfirm)}>
                  Yes, Remove
                </Button>
                <Button size="sm" variant="outline" onClick={() => setScopeDeleteConfirm(null)}>
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Bundle discount preview — no dollar amounts */}
        {isMultiScope && (
          <div className="flex items-center gap-3 p-4 rounded-2xl border-2 border-primary/20 bg-primary/5 animate-fade-in">
            <Sparkles className="h-5 w-5 shrink-0" style={{ color: brandColor }} />
            <div className="flex-1">
              <p className="text-sm font-bold text-foreground">Multi-Project Bundle Savings</p>
              <p className="text-xs text-muted-foreground mt-0.5">5% bundle discount will be applied to your combined project</p>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-4 w-full">
          <button onClick={handleAddAnotherScope}
            className="flex flex-col items-center gap-3 p-6 rounded-2xl border-2 border-dashed border-border hover:border-primary/40 hover:bg-primary/5 transition-all duration-200 group w-full">
            <div className="w-12 h-12 rounded-xl bg-muted/50 group-hover:bg-primary/10 flex items-center justify-center transition-colors">
              <Plus className="h-6 w-6 text-muted-foreground group-hover:text-primary transition-colors" />
            </div>
            <div className="text-center">
              <p className="text-sm font-bold text-foreground">Add Another Project</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">Windows, Doors, Another Bath…</p>
            </div>
          </button>
          {/* <button onClick={handleProceedToPricing}
            className="flex flex-col items-center gap-3 p-6 rounded-2xl border-2 border-primary/30 transition-all duration-200 hover:shadow-md"
            style={{ background: brandColorLight }}>
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: brandColorMid }}>
              <ArrowRight className="h-6 w-6" style={{ color: brandColor }} />
            </div>
            <div className="text-center">
              <p className="text-sm font-bold" style={{ color: brandColor }}>Continue to Pricing</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">Review investment & close</p>
            </div>
          </button> */}
        </div>
      </div>
    );
  };

  /* ── STEPS 8+9: Full Closing Presentation ── */
  const renderClosingPresentation = () => {
    const ITEM_ICONS_EXTRA: Record<string, React.ElementType> = { ...ITEM_ICONS, Layout: Square, Enclosure: GlassWater, "Hardware Finish": Wrench };
    const SCOPE_ICONS: Record<string, React.ElementType> = {
      "Shower Remodel": ShowerHead, "Bath Conversion": Bath, "Full Bathroom Remodel": Hammer,
      "Walk-in Tub": Bath, "Windows": Square, "Doors": DoorOpen,
    };

    // Build items for each scope
    const allScopeBuildItems = completedScopes.map(s => {
      const items = deriveBuildSummary(s.config, allOptions);
      // Add missing items
      if (s.config.layoutType && !items.find(i => i.label === "Layout")) items.unshift({ label: "Layout", value: s.config.layoutType, category: "core" as const });
      if (s.config.enclosureType && !items.find(i => i.label === "Enclosure")) items.push({ label: "Enclosure", value: s.config.enclosureType, category: "core" as const });
      if (s.config.hardwareFinish && !items.find(i => i.label === "Hardware Finish")) items.push({ label: "Hardware Finish", value: s.config.hardwareFinish, category: "accessory" as const });
      if (s.config.baseWidth && s.config.baseLength && !items.find(i => i.label === "Base Size")) items.splice(1, 0, { label: "Base Size", value: `${s.config.baseWidth}" × ${s.config.baseLength}"`, category: "core" as const });
      if (s.noAddOns && !items.find(i => i.label === "Add-ons")) items.push({ label: "Add-ons", value: "No Additional Add-Ons", category: "addon" as const });
      return { scope: s, items };
    });

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-0 -mx-5 -my-4 h-full">
        {/* LEFT PANE — All scopes build summary */}
        <div className="px-8 py-6 md:border-r border-b md:border-b-0 overflow-y-auto max-h-[70vh]" style={{ background: `linear-gradient(180deg, ${brandColorLight}, transparent)` }}>
          <p className="text-xs font-bold uppercase tracking-[0.18em] mb-2" style={{ color: brandColor }}>
            {isMultiScope ? "Your Custom Projects" : "Your Custom Project"}
          </p>

          {allScopeBuildItems.map(({ scope: s, items }, scopeIdx) => {
            const SIcon = SCOPE_ICONS[s.category] || Package;
            return (
              <div key={s.id} className={scopeIdx > 0 ? "mt-6 pt-6 border-t border-border/30" : ""}>
                {isMultiScope && (
                  <div className="flex items-center gap-3 mb-4 p-3 rounded-xl" style={{ background: brandColorMid }}>
                    <SIcon className="h-5 w-5" style={{ color: brandColor }} />
                    <p className="text-sm font-bold" style={{ color: brandColor }}>
                      Scope {scopeIdx + 1}: {s.category}
                    </p>
                    <Badge className="ml-auto text-[9px] h-5" style={{ background: brandColorMid, color: brandColor }}>
                      ${(completedScopePrices[scopeIdx] || 0).toLocaleString()}
                    </Badge>
                  </div>
                )}
                <div className="space-y-2.5">
                  {items.map(item => {
                    const Icon = ITEM_ICONS_EXTRA[item.label] || getItemIcon(item.label);
                    return (
                      <div key={`${s.id}-${item.label}`} className="flex items-start gap-3.5 p-3.5 rounded-xl border border-border/40 bg-card/80 shadow-sm backdrop-blur-sm">
                        <div className="mt-0.5 p-2.5 rounded-xl shrink-0 shadow-inner" style={{ background: brandColorMid }}>
                          <Icon className="h-4 w-4" style={{ color: brandColor }} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">{item.label}</p>
                          <p className="text-sm font-semibold mt-0.5 leading-snug text-foreground">{item.value}</p>
                        </div>
                        <CheckCircle className="h-4 w-4 mt-1 shrink-0 opacity-40" style={{ color: brandColor }} />
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* Bundle discount in left pane */}
          {isMultiScope && (
            <div className="mt-6 pt-4 border-t border-border/30">
              <div className="flex items-center gap-3 p-3 rounded-xl bg-primary/5 border border-primary/20">
                <Sparkles className="h-4 w-4" style={{ color: brandColor }} />
                <p className="text-xs font-semibold flex-1">Multi-Project Bundle Discount</p>
                <p className="text-sm font-bold" style={{ color: brandColor }}>-${bundleDiscount.toLocaleString()}</p>
              </div>
            </div>
          )}

          {/* Scope of work for first scope */}
          {completedScopes.length > 0 && (() => {
            const firstScope = deriveScope(completedScopes[0].config);
            return firstScope && firstScope.sections.length > 0 ? (
              <div className="mt-7 pt-6 border-t border-border/30">
                <p className="text-xs font-bold uppercase tracking-[0.18em] mb-4" style={{ color: brandColor }}>What's Included</p>
                <div className="space-y-4">{firstScope.sections.map((section, i) => (<div key={i}><p className="text-xs font-bold mb-2 text-foreground">{section.title}</p><ul className="space-y-1.5">{section.items.map((sItem, j) => (<li key={j} className="text-xs text-muted-foreground flex items-start gap-2"><Check className="h-3.5 w-3.5 mt-0.5 shrink-0" style={{ color: brandColor }} /><span>{sItem}</span></li>))}</ul></div>))}</div>
              </div>
            ) : null;
          })()}

          <div className="mt-7 pt-6 border-t border-border/30">
            <p className="text-xs font-bold uppercase tracking-[0.18em] mb-3" style={{ color: brandColor }}>Customer</p>
            <p className="text-base font-bold text-foreground">{arrival.name}</p>
            <div className="flex flex-wrap gap-3 mt-2 text-xs text-muted-foreground">
              {arrival.address && <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" style={{ color: brandColor }} />{arrival.address}</span>}
              {arrival.phone && <span className="inline-flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" style={{ color: brandColor }} />{arrival.phone}</span>}
              {arrival.primaryEmail && <span className="inline-flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" style={{ color: brandColor }} />{arrival.primaryEmail}</span>}
            </div>
          </div>
        </div>
        {/* RIGHT PANE — Pricing flow */}
        <div className="px-8 py-6 flex flex-col min-h-[400px]">
          <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-[0.15em] mb-4">{isCloseLayer ? "Let's Get Started" : "Investment"}</p>
          <div className="flex-1 flex flex-col justify-center">
            {/* Stage: hidden — Show button to begin */}
            {closeStage === "hidden" && (<div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4" style={{ background: brandColorMid }}><Eye className="h-7 w-7" style={{ color: brandColor }} /></div>
              <p className="text-sm text-muted-foreground mb-6">Ready to present pricing options</p>
              <Button size="lg" className="h-14 px-10 text-base font-semibold rounded-xl shadow-lg" style={{ background: brandColor }} onClick={() => advanceCloseStage()}>Show Pricing Options <ChevronRight className="h-5 w-5 ml-1" /></Button>
            </div>)}

            {/* Stage: standard — 1-Year Standard Price shown FIRST */}
            {closeStage === "standard" && (<div className="py-4 space-y-5 animate-in fade-in-0 slide-in-from-bottom-4 duration-500">
               <div className="rounded-2xl p-8 border bg-gradient-to-br from-card to-muted/30 shadow-sm text-center">
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.18em] mb-3">Standard Investment</p>
                <p className="text-5xl font-bold tracking-tight">${standardPrice.toLocaleString()}</p>
                {isMultiScope && (
                  <div className="mt-3 space-y-1">
                    <p className="text-xs font-semibold" style={{ color: brandColor }}>
                      {completedScopes.length} projects combined · 5% bundle savings applied
                    </p>
                    <p className="text-[10px] text-muted-foreground line-through">${combinedScopeTotal.toLocaleString()} before bundle</p>
                  </div>
                )}
                <div className="mt-4 space-y-1">
                  <p className="text-xs text-muted-foreground">This quote is valid for one year from today</p>
                  <p className="text-[10px] text-muted-foreground/70">Valid through {oneYearExpiration}</p>
                </div>
              </div>
              <Button size="lg" className="w-full h-14 text-base font-semibold rounded-xl shadow-lg gap-2" style={{ background: brandColor }}
                onClick={() => advanceCloseStage()}>
                <Sparkles className="h-5 w-5" /> Check Current Promotions <ChevronRight className="h-5 w-5" />
              </Button>
            </div>)}

            {/* Stage: transition — Value reinforcement reel */}
            {closeStage === "transition" && (<BestValueTransition brandColor={brandColor} brandColorLight={brandColorLight} brandColorMid={brandColorMid} companyName={branding.companyName} onComplete={() => advanceCloseStage()} />)}

            {/* Stage: pricing — Best Value reveal */}
            {closeStage === "pricing" && (<div className="py-6 animate-in fade-in-0 duration-500">

              {/* Cash vs Finance selection — before reveal */}
              {!bestValueRevealed && !bestValueCalculating && (
                <div className="text-center space-y-8">
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">One Last Step</p>
                    <h3 className="text-xl font-bold tracking-tight text-foreground leading-snug max-w-sm mx-auto">
                      Would you prefer to handle this with cash or explore financing?
                    </h3>
                  </div>
                  <div className="grid grid-cols-2 gap-4 max-w-xs mx-auto">
                    <button onClick={() => handleBestValueSelect("cash")}
                      className={`flex flex-col items-center gap-3 p-6 rounded-2xl border-2 transition-all hover:shadow-md active:scale-[0.98] ${bestValueSelection === "cash" ? "border-primary bg-primary/5 shadow-md" : "border-border hover:border-muted-foreground/30"}`}>
                      <div className="p-3.5 rounded-xl" style={{ background: brandColorMid }}>
                        <Banknote className="h-7 w-7" style={{ color: brandColor }} />
                      </div>
                      <span className="text-sm font-bold">Cash</span>
                    </button>
                    <button onClick={() => handleBestValueSelect("financing")}
                      className={`flex flex-col items-center gap-3 p-6 rounded-2xl border-2 transition-all hover:shadow-md active:scale-[0.98] ${bestValueSelection === "financing" ? "border-primary bg-primary/5 shadow-md" : "border-border hover:border-muted-foreground/30"}`}>
                      <div className="p-3.5 rounded-xl" style={{ background: brandColorMid }}>
                        <CreditCard className="h-7 w-7" style={{ color: brandColor }} />
                      </div>
                      <span className="text-sm font-bold">Finance</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Calculating animation */}
              {bestValueCalculating && (
                <div className="rounded-2xl border bg-card p-12 text-center animate-in fade-in-0 duration-300">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-full mb-4 animate-pulse" style={{ background: brandColorMid }}>
                    <Sparkles className="h-7 w-7" style={{ color: brandColor }} />
                  </div>
                  <p className="text-sm font-semibold text-foreground">Calculating best value options…</p>
                  <div className="w-36 mx-auto mt-4"><Progress value={66} className="h-1.5" /></div>
                </div>
              )}

              {/* Best Value Revealed — clean, dominant pricing */}
              {bestValueRevealed && (
                <div className="text-center space-y-8 animate-in fade-in-0 slide-in-from-bottom-4 duration-700">
                  {/* Headline */}
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Your Best Value Today</p>

                  {/* Dominant price */}
                  <div className="py-4">
                    <p className="text-6xl sm:text-7xl font-extrabold tracking-tight" style={{ color: brandColor }}>
                      ${(bestValueSelection === "cash" ? safeCashPrice : safeFinancePrice).toLocaleString()}
                    </p>
                    <p className="text-sm text-muted-foreground mt-3">After all applicable savings</p>
                  </div>

                  {/* Minimal breakdown */}
                  <div className="max-w-[240px] mx-auto space-y-2 py-4 border-t border-b border-border">
                    <div className="flex justify-between text-sm text-muted-foreground">
                      <span>Retail Price</span>
                      <span>${standardPrice.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm font-semibold" style={{ color: brandColor }}>
                      <span>Savings Applied</span>
                      <span>–${(standardPrice - (bestValueSelection === "cash" ? safeCashPrice : safeFinancePrice)).toLocaleString()}</span>
                    </div>
                  </div>

                  {/* Confidence line */}
                  <p className="text-sm text-muted-foreground font-medium">Everything included. No surprises.</p>

                  {/* Toggle */}
                  <div className="flex items-center justify-center gap-3">
                    <button onClick={() => setBestValueSelection("cash")}
                      className={`px-5 py-2.5 rounded-xl text-xs font-semibold transition-all ${bestValueSelection === "cash" ? "bg-primary text-primary-foreground shadow-md" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}>
                      <Banknote className="h-3.5 w-3.5 inline mr-1.5" />Cash
                    </button>
                    <button onClick={() => setBestValueSelection("financing")}
                      className={`px-5 py-2.5 rounded-xl text-xs font-semibold transition-all ${bestValueSelection === "financing" ? "bg-primary text-primary-foreground shadow-md" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}>
                      <CreditCard className="h-3.5 w-3.5 inline mr-1.5" />Finance
                    </button>
                  </div>

                  {/* CTA */}
                  <Button size="lg" className="w-full h-14 text-base font-semibold rounded-2xl shadow-lg" style={{ background: brandColor }} onClick={() => { setSelectedPayment(bestValueSelection); advanceCloseStage(); }}>
                    Project looks great! <ChevronRight className="h-5 w-5 ml-1" />
                  </Button>
                </div>
              )}
            </div>)}

            {closeStage === "photos" && (<div className="py-6 animate-in fade-in-0 slide-in-from-bottom-4 duration-500">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-4" style={{ background: brandColorMid }}><Camera className="h-7 w-7" style={{ color: brandColor }} /></div>
                <h3 className="text-lg font-bold mb-1">Capture Project Photos</h3>
                <p className="text-sm text-muted-foreground">Photos are required before generating the contract</p>
              </div>
              <div className="space-y-2 mb-6">
                {["Full project area", "Measurement references", "Obstructions or special conditions"].map(item => (
                  <div key={item} className="flex items-center gap-2.5 p-3 rounded-lg border">
                    <Camera className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="text-xs font-medium">{item}</span>
                  </div>
                ))}
              </div>
              <button type="button" onClick={() => setPhotosConfirmed(!photosConfirmed)} className={`w-full flex items-center gap-3 p-3.5 rounded-lg border-2 text-left transition-all mb-4 ${photosConfirmed ? "border-emerald-500/40 bg-emerald-500/5" : "border-amber-500/40 bg-amber-500/5"}`}>
                <SelectionIndicator checked={photosConfirmed} />
                <div className="flex items-center gap-2">
                  <Camera className={`h-4 w-4 ${photosConfirmed ? "text-emerald-600" : "text-amber-600"}`} />
                  <span className="text-xs font-semibold">{photosConfirmed ? "Photos captured and attached" : "Confirm all required photos have been captured"}</span>
                </div>
              </button>
              <div className="rounded-xl border p-4 text-center mb-4 bg-muted/20">
                <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-1">{selectedPayment === "financing" ? "Amount Due Today" : "Deposit (50%)"}</p>
                <p className="text-2xl font-bold" style={{ color: brandColor }}>{depositAmount === 0 ? "$0" : `$${depositAmount.toLocaleString()}`}</p>
                <p className="text-[10px] text-muted-foreground mt-1">Project Total: ${chosenPrice.toLocaleString()} · {selectedPayment === "financing" ? "Financing" : "Cash / Check"}</p>
              </div>
              <Button size="lg" className="w-full h-14 text-base font-semibold rounded-xl shadow-lg" style={{ background: brandColor }} onClick={() => advanceCloseStage()} disabled={!photosConfirmed}>Continue to Agreement <ChevronRight className="h-5 w-5 ml-1" /></Button>
              <button onClick={goBackCloseStage} className="mt-4 text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 mx-auto"><ArrowLeft className="h-3 w-3" /> Back</button>
            </div>)}
            {closeStage === "contract" && (<div className="py-4 animate-in fade-in-0 slide-in-from-bottom-4 duration-500">
              <div className="flex items-center gap-2 mb-4"><FileText className="h-4 w-4" style={{ color: brandColor }} /><h3 className="text-base font-bold">Agreement</h3></div>
              <div className="rounded-lg border bg-muted/20 p-3 mb-4 text-xs space-y-1.5">
                <div className="flex justify-between"><span className="text-muted-foreground">Customer</span><span className="font-medium">{arrival.name}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Project Total</span><span className="font-bold" style={{ color: brandColor }}>${chosenPrice.toLocaleString()}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Payment</span><span className="font-medium">{selectedPayment === "financing" ? "Financing" : "Cash / Check"}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Due Today</span><span className="font-medium">{depositAmount === 0 ? "$0 Down" : `$${depositAmount.toLocaleString()}`}</span></div>
              </div>
              <div className="space-y-2.5 mb-4">
                <div className="flex items-start gap-3 p-2.5 rounded-lg border"><Checkbox id="flow-terms" checked={termsAccepted} onCheckedChange={(c) => setTermsAccepted(!!c)} /><label htmlFor="flow-terms" className="text-[11px] leading-relaxed cursor-pointer">I agree to the terms and conditions and authorize {branding.companyName} to perform the work at the stated price.</label></div>
                <div className="flex items-start gap-3 p-2.5 rounded-lg border"><Checkbox id="flow-rescission" checked={rescissionAcknowledged} onCheckedChange={(c) => setRescissionAcknowledged(!!c)} /><label htmlFor="flow-rescission" className="text-[11px] leading-relaxed cursor-pointer">I acknowledge the 3-business-day right of rescission.</label></div>
              </div>
              <div className="space-y-3 mb-4">
                <div><SignatureCapture label="Homeowner Signature" onCapture={setHomeownerSig} existingSignature={homeownerSig || undefined} />{homeownerSig && <p className="text-[10px] text-emerald-600 flex items-center gap-1 mt-1"><CheckCircle className="h-3 w-3" /> Captured</p>}</div>
                <div><SignatureCapture label="Company Representative" onCapture={setRepSig} existingSignature={repSig || undefined} />{repSig && <p className="text-[10px] text-emerald-600 flex items-center gap-1 mt-1"><CheckCircle className="h-3 w-3" /> Captured</p>}</div>
              </div>
              <div className="flex items-start gap-2 text-[10px] text-muted-foreground bg-muted/30 p-2.5 rounded-lg mb-4"><Shield className="h-3.5 w-3.5 shrink-0 mt-0.5" /><span>Contract documents are securely stored and tied to your customer profile.</span></div>
              <Button size="lg" className="w-full h-14 text-base font-semibold rounded-xl shadow-lg gap-2" style={{ background: canSign ? brandColor : undefined }} disabled={!canSign} onClick={handleSignContract}><CheckCircle className="h-5 w-5" /> Sign & Approve Agreement</Button>
              <button onClick={goBackCloseStage} className="mt-4 text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 mx-auto"><ArrowLeft className="h-3 w-3" /> Back</button>
            </div>)}
            {closeStage === "confirmed" && (<div className="text-center py-8 animate-in fade-in-0 slide-in-from-bottom-4 duration-500">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-5" style={{ background: brandColorMid }}><CheckCircle className="h-10 w-10" style={{ color: brandColor }} /></div>
              <h3 className="text-2xl font-bold mb-2">You're All Set!</h3>
              <p className="text-sm text-muted-foreground mb-5 max-w-sm mx-auto leading-relaxed">Your project is confirmed. Our team will reach out shortly to schedule your installation.</p>

              <div className="grid grid-cols-3 gap-2 max-w-sm mx-auto mb-6">
                <button className="flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 border-border hover:border-primary/40 transition-all" onClick={() => setContractViewOpen(!contractViewOpen)}>
                  <FileText className="h-5 w-5" style={{ color: brandColor }} />
                  <span className="text-[10px] font-semibold">{contractViewOpen ? "Hide Contract" : "View Contract"}</span>
                </button>
                <button className="flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 border-border hover:border-primary/40 transition-all" onClick={() => {
                  generateContractPDF({
                    companyName: branding.companyName,
                    contractId: `CTR-${Date.now()}`,
                    date: new Date().toLocaleDateString(),
                    arrival,
                    discovery,
                    config,
                    allOptions,
                    completedScopes,
                    isMultiScope,
                    pricing: { standardPrice, promoPrice, chosenPrice, selectedPayment, depositAmount },
                    homeownerSig: homeownerSig || undefined,
                    repSig: repSig || undefined,
                    homeownerSignedAt: homeownerSig ? new Date().toISOString() : undefined,
                    repSignedAt: repSig ? new Date().toISOString() : undefined,
                  });
                  toast.success("Contract PDF downloaded", { description: `${arrival.name} — ${discovery.category || "Project"} contract package` });
                }}>
                  <Package className="h-5 w-5" style={{ color: brandColor }} />
                  <span className="text-[10px] font-semibold">Download PDF</span>
                </button>
                <button className="flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 border-border hover:border-primary/40 transition-all" onClick={() => setSendDialogOpen(true)}>
                  <Mail className="h-5 w-5" style={{ color: brandColor }} />
                  <span className="text-[10px] font-semibold">Send to Homeowner</span>
                </button>
              </div>

              {contractViewOpen && (
                <div className="max-w-lg mx-auto mb-6 rounded-xl border bg-card p-5 text-left space-y-4 animate-in fade-in-0 slide-in-from-top-2 duration-300">
                  <div className="border-b pb-3">
                    <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Agreement</p>
                    <p className="text-sm font-bold mt-1">{branding.companyName} — Service Agreement</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">Date: {new Date().toLocaleDateString()}</p>
                  </div>

                  <div className="space-y-1.5 text-xs border-b pb-3">
                    <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">Customer Information</p>
                    <div className="flex justify-between"><span className="text-muted-foreground">Name</span><span className="font-medium">{arrival.name}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Phone</span><span className="font-medium">{arrival.phone}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Email</span><span className="font-medium">{arrival.primaryEmail}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Address</span><span className="font-medium truncate max-w-[60%] text-right">{arrival.address}</span></div>
                    {arrival.secondaryName && (
                      <>
                        <div className="flex justify-between"><span className="text-muted-foreground">Secondary Contact</span><span className="font-medium">{arrival.secondaryName}</span></div>
                        {arrival.secondaryPhone && <div className="flex justify-between"><span className="text-muted-foreground">Secondary Phone</span><span className="font-medium">{arrival.secondaryPhone}</span></div>}
                        {arrival.secondaryEmail && <div className="flex justify-between"><span className="text-muted-foreground">Secondary Email</span><span className="font-medium">{arrival.secondaryEmail}</span></div>}
                      </>
                    )}
                  </div>

                  <div className="space-y-1.5 text-xs border-b pb-3">
                    <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">Project Discovery</p>
                    <div className="flex justify-between"><span className="text-muted-foreground">Project Type</span><span className="font-medium">{discovery.category || "Not specified"}</span></div>
                    {discovery.painPoints.length > 0 && (
                      <div className="flex justify-between"><span className="text-muted-foreground">Pain Points</span><span className="font-medium text-right max-w-[50%]">{discovery.painPoints.join(", ")}</span></div>
                    )}
                    {discovery.notes && (
                      <div className="mt-1"><span className="text-muted-foreground">Notes: </span><span className="font-medium">{discovery.notes}</span></div>
                    )}
                  </div>

                  <div className="space-y-1.5 text-xs border-b pb-3">
                    <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">Site Assessment</p>
                    <div className="flex justify-between"><span className="text-muted-foreground">Layout</span><span className="font-medium">{config.layoutType || "Not specified"}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Window in Shower</span><span className="font-medium">{config.hasWindow ? "Yes" : "No"}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Obstructions</span><span className="font-medium">{config.obstructions?.length ? config.obstructions.join(", ") : "None"}</span></div>
                  </div>

                  <div className="space-y-1.5 text-xs border-b pb-3">
                    <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">Measurements</p>
                    {config.wallMeasurements?.wallA?.width && (
                      <div className="flex justify-between"><span className="text-muted-foreground">Wall A</span><span className="font-medium">{config.wallMeasurements.wallA.width}" × {config.wallMeasurements.wallA.height}"</span></div>
                    )}
                    {config.wallMeasurements?.wallB?.width && (
                      <div className="flex justify-between"><span className="text-muted-foreground">Wall B</span><span className="font-medium">{config.wallMeasurements.wallB.width}" × {config.wallMeasurements.wallB.height}"</span></div>
                    )}
                    {config.wallMeasurements?.wallC?.width && (
                      <div className="flex justify-between"><span className="text-muted-foreground">Wall C</span><span className="font-medium">{config.wallMeasurements.wallC.width}" × {config.wallMeasurements.wallC.height}"</span></div>
                    )}
                    {config.drainLocation && <div className="flex justify-between"><span className="text-muted-foreground">Drain Location</span><span className="font-medium">{config.drainLocation}</span></div>}
                  </div>

                  <div className="space-y-1.5 text-xs border-b pb-3">
                    <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">Structural Changes</p>
                    <div className="flex justify-between"><span className="text-muted-foreground">Removing Walls</span><span className="font-medium">{config.structuralChanges?.removingWalls ? "Yes" : "No"}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Adding Walls</span><span className="font-medium">{config.structuralChanges?.addingWalls ? "Yes" : "No"}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Moving Drain</span><span className="font-medium">{config.structuralChanges?.movingDrain ? "Yes" : "No"}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Moving Plumbing</span><span className="font-medium">{config.structuralChanges?.movingPlumbing ? "Yes" : "No"}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Subfloor Modification</span><span className="font-medium">{config.structuralChanges?.subfloorMod ? "Yes" : "No"}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Window Modification</span><span className="font-medium">{config.structuralChanges?.windowMod || "N/A"}</span></div>
                  </div>

                  {completedScopes.map((s, idx) => {
                    const scopeItems = deriveBuildSummary(s.config, allOptions);
                    return (
                      <div key={s.id} className="border-b pb-3">
                        <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">
                          {isMultiScope ? `Scope ${idx + 1}: ${s.category}` : "Design Selections"}
                        </p>
                        <div className="space-y-1">{scopeItems.map((item, i) => (<div key={i} className="flex justify-between text-xs"><span className="text-muted-foreground">{item.label}</span><span className="font-medium">{item.value || "—"}</span></div>))}</div>
                      </div>
                    );
                  })}

                  {config.discounts && (
                    <div className="border-b pb-3">
                      <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">Discounts Applied</p>
                      <div className="space-y-1 text-xs">
                        <div className="flex justify-between"><span className="text-muted-foreground">Military</span><span className="font-medium">{config.discounts.military ? "Applied" : "—"}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">Senior</span><span className="font-medium">{config.discounts.senior ? "Applied" : "—"}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">Frontliner</span><span className="font-medium">{config.discounts.frontliner ? "Applied" : "—"}</span></div>
                      </div>
                    </div>
                  )}

                  <div className="border-b pb-3">
                    <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">Pricing Summary</p>
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between"><span className="text-muted-foreground">Standard Price</span><span>${standardPrice.toLocaleString()}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Promotional Price</span><span>${promoPrice.toLocaleString()}</span></div>
                      <div className="flex justify-between font-bold"><span>Contract Total</span><span style={{ color: brandColor }}>${chosenPrice.toLocaleString()}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Payment Method</span><span>{selectedPayment === "financing" ? "Financing" : "Cash / Check"}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Deposit</span><span>{depositAmount === 0 ? "$0 Down" : `$${depositAmount.toLocaleString()}`}</span></div>
                    </div>
                  </div>
                  <div className="pb-1">
                    <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">Photos</p>
                    <div className="flex items-center gap-2 text-xs"><CheckCircle className="h-3.5 w-3.5 text-emerald-600" /><span>Project photos captured and attached</span></div>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">Signatures</p>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        {homeownerSig ? (
                          <img src={homeownerSig} alt="Homeowner signature" className="h-8 object-contain" />
                        ) : (
                          <CheckCircle className="h-3.5 w-3.5 text-emerald-600" />
                        )}
                        <span className="text-xs">{arrival.name} — {homeownerSig ? "Signed" : "Pending"}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {repSig ? (
                          <img src={repSig} alt="Rep signature" className="h-8 object-contain" />
                        ) : (
                          <CheckCircle className="h-3.5 w-3.5 text-emerald-600" />
                        )}
                        <span className="text-xs">Company Rep — {repSig ? "Signed" : "Pending"}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="rounded-xl border p-4 max-w-sm mx-auto text-left space-y-2 mb-5 bg-muted/20">
                <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">Contract Package Includes</p>
                <div className="flex items-center gap-2 text-xs"><Check className="h-3.5 w-3.5 shrink-0" style={{ color: brandColor }} /><span>Agreement (Terms & Conditions)</span></div>
                <div className="flex items-center gap-2 text-xs"><Check className="h-3.5 w-3.5 shrink-0" style={{ color: brandColor }} /><span>Work Order — Full build breakdown & materials</span></div>
                <div className="flex items-center gap-2 text-xs"><Check className="h-3.5 w-3.5 shrink-0" style={{ color: brandColor }} /><span>Project Photos</span></div>
                <div className="flex items-center gap-2 text-xs"><Check className="h-3.5 w-3.5 shrink-0" style={{ color: brandColor }} /><span>Final Pricing Summary</span></div>
              </div>

              <div className="rounded-xl border p-4 max-w-sm mx-auto text-left space-y-3 mb-5">
                <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">What Happens Next</p>
                <div className="flex items-start gap-3"><CalendarCheck className="h-4 w-4 mt-0.5 shrink-0" style={{ color: brandColor }} /><p className="text-xs">We'll contact you within 24–48 hours to schedule your installation date.</p></div>
                <div className="flex items-start gap-3"><FileText className="h-4 w-4 mt-0.5 shrink-0" style={{ color: brandColor }} /><p className="text-xs">A copy of your agreement will be sent to your email on file.</p></div>
                <div className="flex items-start gap-3"><Phone className="h-4 w-4 mt-0.5 shrink-0" style={{ color: brandColor }} /><p className="text-xs">Questions? Call us anytime — we're here to help.</p></div>
              </div>

              <Card className="max-w-sm mx-auto border-amber-500/30 bg-amber-500/5 mb-5">
                <CardContent className="p-3 text-xs text-amber-700 flex items-start gap-2">
                  <Shield className="h-4 w-4 shrink-0 mt-0.5" />
                  <span>Build configuration, pricing, and materials are now locked. Any changes require a Change Order.</span>
                </CardContent>
              </Card>

              <p className="text-lg font-bold" style={{ color: brandColor }}>Thank you for choosing {branding.companyName}!</p>
              <Button variant="outline" size="sm" className="mt-4" onClick={onClose}>Done</Button>
            </div>)}
          </div>
          <div className="flex items-center justify-center gap-1.5 mt-4 pt-4 border-t">
            {Array.from({ length: closeDotCount }).map((_, i) => (<div key={`c-${i}`} className="h-1.5 rounded-full transition-all duration-300" style={{ width: closeProgress >= i + 1 ? 20 : 6, background: closeProgress >= i + 1 ? brandColor : "hsl(var(--muted))" }} />))}
          </div>
        </div>
      </div>
    );
  };

  const isPresentationStep = step.id === "price" || step.id === "close";
  const renderStep = () => {
    // Show scope gate when it's active (between design and discounts)
    if (showScopeGate) return renderScopeGate();
    switch (step.id) {
      case "arrival": return renderArrival();
      case "discovery": return renderDiscovery();
      case "site": return renderSite();
      case "measure": return renderMeasure();
      case "structure": return renderStructure();
      case "design": return renderDesign();
      case "discounts": return renderDiscounts();
      case "price":
      case "close":
        return renderClosingPresentation();
      default: return null;
    }
  };

  /* ═══════════════════════════════════════════════════════
     Layout — Premium Consultation Shell
     ═══════════════════════════════════════════════════════ */
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { saveConsultationProgress(); onClose(); }}}>
      <DialogContent className="w-screen h-screen max-w-[100vw] max-h-[100vh] rounded-none p-0 gap-0 flex flex-col overflow-hidden border-0 shadow-none"
        style={{ width: '100vw', height: '100vh' }}>
        {/* Header — Premium branded bar */}
        <div className="px-10 py-4 border-b shrink-0" style={{ background: `linear-gradient(135deg, ${brandColorLight}, hsl(var(--card)))` }}>
          <div className="flex items-center gap-5">
            <div className="h-12 w-12 rounded-2xl flex items-center justify-center shrink-0 shadow-md" style={{ background: brandColorMid }}>
              <User className="h-5 w-5" style={{ color: brandColor }} />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-xl font-bold text-foreground leading-tight truncate">{arrival.name || lead.primaryName}</h2>
              <div className="text-sm text-muted-foreground flex items-center gap-2 mt-0.5">
                <MapPin className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{arrival.address || `${lead.address}, ${lead.city}`}</span>
                {completedScopes.length > 1 && (
                  <>
                    <span className="text-muted-foreground/30">·</span>
                    <Badge className="text-[10px] h-5 px-2 shrink-0 font-semibold" style={{ background: brandColorMid, color: brandColor }}>
                      {completedScopes.length} Scopes
                    </Badge>
                  </>
                )}
                {completedScopes.length <= 1 && discovery.category && (
                  <>
                    <span className="text-muted-foreground/30">·</span>
                    <Badge className="text-[10px] h-5 px-2 shrink-0 font-semibold" style={{ background: brandColorMid, color: brandColor }}>{discovery.category === "Shower Remodel" ? "Walk-in Shower" : discovery.category}</Badge>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Progress — Premium step indicator */}
        <div className="px-10 pt-5 pb-4 border-b shrink-0 bg-gradient-to-b from-muted/20 to-transparent">
          {step.headline && (
            <div className="mb-5">
              <h3 className="text-2xl font-bold text-foreground leading-tight">{step.headline}</h3>
              <p className="text-base text-muted-foreground mt-1.5 leading-relaxed">{step.subtext}</p>
            </div>
          )}
          <div className="flex items-center gap-3 mb-2.5">
            <span className="text-xs font-bold text-muted-foreground">
              Step {currentStep + 1} of {FLOW_STEPS.length}
            </span>
            <div className="flex-1" />
            <span className="text-xs font-bold" style={{ color: brandColor }}>{Math.round(progress)}%</span>
          </div>
          <div className="h-2 rounded-full bg-muted/40 overflow-hidden">
            <div className="h-full rounded-full transition-all duration-500 ease-out" style={{ width: `${progress}%`, background: `linear-gradient(90deg, ${brandColor}, hsl(${branding.primaryHue} ${Math.min(branding.primarySaturation + 15, 100)}% ${Math.min(branding.primaryLightness + 10, 70)}%))` }} />
          </div>
          <div className="flex gap-1.5 mt-3 overflow-x-auto pb-0.5">
            {FLOW_STEPS.map((s, i) => {
              const Icon = s.icon;
              const isActive = i === currentStep;
              const isComplete = i < currentStep;
              return (
                <button key={s.id}
                  onClick={() => { if (i < currentStep) setCurrentStep(i); }}
                  disabled={i > currentStep}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-semibold transition-all duration-200 whitespace-nowrap shrink-0 ${
                    isActive ? "text-primary-foreground shadow-md"
                    : isComplete ? "bg-primary/10 text-primary hover:bg-primary/15 cursor-pointer"
                    : "bg-muted/40 text-muted-foreground/50 cursor-not-allowed"
                  }`}
                  style={isActive ? { background: brandColor } : undefined}>
                  {isComplete ? <Check className="h-3.5 w-3.5" /> : <Icon className="h-3.5 w-3.5" />}
                  {s.shortLabel}
                </button>
              );
            })}
          </div>
        </div>

        {/* Step Content */}
        <ScrollArea className="flex-1 bg-gradient-to-b from-background via-background to-muted/10">
          <div className={`${isPresentationStep ? "px-10 py-8" : "px-8 py-8 max-w-3xl mx-auto"}`}>
            {renderStep()}
          </div>
        </ScrollArea>

        {/* Footer Navigation — Premium, confident */}
        {!isPresentationStep && step.id !== "close" && (
          <div className="flex items-center justify-between px-10 py-5 border-t shrink-0" style={{ background: `linear-gradient(135deg, hsl(var(--card)), ${brandColorLight})` }}>
            <Button variant="ghost" size="sm" className="h-12 text-sm gap-2 text-muted-foreground hover:text-foreground" onClick={handleBack} disabled={currentStep === 0}>
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            <Button size="sm" className="h-12 px-10 text-sm font-bold gap-2 rounded-xl shadow-lg" style={{ background: canAdvance && !isNavigating ? brandColor : undefined }}
              onClick={handleNext} disabled={!canAdvance || isNavigating}>
              {currentStep === FLOW_STEPS.length - 2 ? "Review Investment" : "Continue"}
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </DialogContent>

      <DesignValidationDialog
        open={designValidationOpen}
        onOpenChange={setDesignValidationOpen}
        result={validateDesignStep(config, isBathroom)}
        onContinue={handleDesignContinueAnyway}
        onGoBack={() => setDesignValidationOpen(false)}
        onProceedToPricing={handleProceedDirectlyToPricing}
      />

      <Dialog open={successModalOpen} onOpenChange={setSuccessModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl p-6">
          <div className="text-center py-8">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-5" style={{ background: brandColorMid }}>
              <CheckCircle className="h-10 w-10" style={{ color: brandColor }} />
            </div>
            <h3 className="text-2xl font-bold mb-2">You're All Set!</h3>
            <p className="text-sm text-muted-foreground mb-5 max-w-sm mx-auto leading-relaxed">Your project is confirmed. Our team will reach out shortly to schedule your installation.</p>

            <div className="grid grid-cols-3 gap-2 max-w-sm mx-auto mb-6">
              <button className="flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 border-border hover:border-primary/40 transition-all" onClick={() => setContractViewOpen(!contractViewOpen)}>
                <FileText className="h-5 w-5" style={{ color: brandColor }} />
                <span className="text-[10px] font-semibold">{contractViewOpen ? "Hide Contract" : "View Contract"}</span>
              </button>
              <button className="flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 border-border hover:border-primary/40 transition-all" onClick={() => {
                generateContractPDF({
                  companyName: branding.companyName,
                  contractId: `CTR-${Date.now()}`,
                  date: new Date().toLocaleDateString(),
                  arrival,
                  discovery,
                  config,
                  allOptions,
                  completedScopes,
                  isMultiScope,
                  pricing: { standardPrice, promoPrice, chosenPrice, selectedPayment, depositAmount },
                  homeownerSig: homeownerSig || undefined,
                  repSig: repSig || undefined,
                  homeownerSignedAt: homeownerSig ? new Date().toISOString() : undefined,
                  repSignedAt: repSig ? new Date().toISOString() : undefined,
                });
                toast.success("Contract PDF downloaded", { description: `${arrival.name} — ${discovery.category || "Project"} contract package` });
              }}>
                <Package className="h-5 w-5" style={{ color: brandColor }} />
                <span className="text-[10px] font-semibold">Download PDF</span>
              </button>
              <button className="flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 border-border hover:border-primary/40 transition-all" onClick={() => setSendDialogOpen(true)}>
                <Mail className="h-5 w-5" style={{ color: brandColor }} />
                <span className="text-[10px] font-semibold">Send to Homeowner</span>
              </button>
            </div>

            {contractViewOpen && (
              <div className="max-w-lg mx-auto mb-6 rounded-xl border bg-card p-5 text-left space-y-4 animate-in fade-in-0 slide-in-from-top-2 duration-300">
                <div className="border-b pb-3">
                  <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Agreement</p>
                  <p className="text-sm font-bold mt-1">{branding.companyName} — Service Agreement</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">Date: {new Date().toLocaleDateString()}</p>
                </div>

                <div className="space-y-1.5 text-xs border-b pb-3">
                  <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">Customer Information</p>
                  <div className="flex justify-between"><span className="text-muted-foreground">Name</span><span className="font-medium">{arrival.name}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Phone</span><span className="font-medium">{arrival.phone}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Email</span><span className="font-medium">{arrival.primaryEmail}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Address</span><span className="font-medium truncate max-w-[60%] text-right">{arrival.address}</span></div>
                  {arrival.secondaryName && (
                    <>
                      <div className="flex justify-between"><span className="text-muted-foreground">Secondary Contact</span><span className="font-medium">{arrival.secondaryName}</span></div>
                      {arrival.secondaryPhone && <div className="flex justify-between"><span className="text-muted-foreground">Secondary Phone</span><span className="font-medium">{arrival.secondaryPhone}</span></div>}
                      {arrival.secondaryEmail && <div className="flex justify-between"><span className="text-muted-foreground">Secondary Email</span><span className="font-medium">{arrival.secondaryEmail}</span></div>}
                    </>
                  )}
                </div>

                <div className="space-y-1.5 text-xs border-b pb-3">
                  <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">Project Discovery</p>
                  <div className="flex justify-between"><span className="text-muted-foreground">Project Type</span><span className="font-medium">{discovery.category || "Not specified"}</span></div>
                  {discovery.painPoints.length > 0 && (
                    <div className="flex justify-between"><span className="text-muted-foreground">Pain Points</span><span className="font-medium text-right max-w-[50%]">{discovery.painPoints.join(", ")}</span></div>
                  )}
                  {discovery.notes && (
                    <div className="mt-1"><span className="text-muted-foreground">Notes: </span><span className="font-medium">{discovery.notes}</span></div>
                  )}
                </div>

                <div className="space-y-1.5 text-xs border-b pb-3">
                  <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">Site Assessment</p>
                  <div className="flex justify-between"><span className="text-muted-foreground">Layout</span><span className="font-medium">{config.layoutType || "Not specified"}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Window in Shower</span><span className="font-medium">{config.hasWindow ? "Yes" : "No"}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Obstructions</span><span className="font-medium">{config.obstructions?.length ? config.obstructions.join(", ") : "None"}</span></div>
                </div>

                <div className="space-y-1.5 text-xs border-b pb-3">
                  <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">Measurements</p>
                  {config.wallMeasurements?.wallA?.width && (
                    <div className="flex justify-between"><span className="text-muted-foreground">Wall A</span><span className="font-medium">{config.wallMeasurements.wallA.width}" × {config.wallMeasurements.wallA.height}"</span></div>
                  )}
                  {config.wallMeasurements?.wallB?.width && (
                    <div className="flex justify-between"><span className="text-muted-foreground">Wall B</span><span className="font-medium">{config.wallMeasurements.wallB.width}" × {config.wallMeasurements.wallB.height}"</span></div>
                  )}
                  {config.wallMeasurements?.wallC?.width && (
                    <div className="flex justify-between"><span className="text-muted-foreground">Wall C</span><span className="font-medium">{config.wallMeasurements.wallC.width}" × {config.wallMeasurements.wallC.height}"</span></div>
                  )}
                  {config.drainLocation && <div className="flex justify-between"><span className="text-muted-foreground">Drain Location</span><span className="font-medium">{config.drainLocation}</span></div>}
                </div>

                <div className="space-y-1.5 text-xs border-b pb-3">
                  <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">Structural Changes</p>
                  <div className="flex justify-between"><span className="text-muted-foreground">Removing Walls</span><span className="font-medium">{config.structuralChanges?.removingWalls ? "Yes" : "No"}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Adding Walls</span><span className="font-medium">{config.structuralChanges?.addingWalls ? "Yes" : "No"}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Moving Drain</span><span className="font-medium">{config.structuralChanges?.movingDrain ? "Yes" : "No"}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Moving Plumbing</span><span className="font-medium">{config.structuralChanges?.movingPlumbing ? "Yes" : "No"}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Subfloor Modification</span><span className="font-medium">{config.structuralChanges?.subfloorMod ? "Yes" : "No"}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Window Modification</span><span className="font-medium">{config.structuralChanges?.windowMod || "N/A"}</span></div>
                </div>

                {completedScopes.map((s, idx) => {
                  const scopeItems = deriveBuildSummary(s.config, allOptions);
                  return (
                    <div key={s.id} className="border-b pb-3">
                      <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">
                        {isMultiScope ? `Scope ${idx + 1}: ${s.category}` : "Design Selections"}
                      </p>
                      <div className="space-y-1">{scopeItems.map((item, i) => (<div key={i} className="flex justify-between text-xs"><span className="text-muted-foreground">{item.label}</span><span className="font-medium">{item.value || "—"}</span></div>))}</div>
                    </div>
                  );
                })}

                {config.discounts && (
                  <div className="border-b pb-3">
                    <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">Discounts Applied</p>
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between"><span className="text-muted-foreground">Military</span><span className="font-medium">{config.discounts.military ? "Applied" : "—"}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Senior</span><span className="font-medium">{config.discounts.senior ? "Applied" : "—"}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Frontliner</span><span className="font-medium">{config.discounts.frontliner ? "Applied" : "—"}</span></div>
                    </div>
                  </div>
                )}

                <div className="border-b pb-3">
                  <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">Pricing Summary</p>
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between"><span className="text-muted-foreground">Standard Price</span><span>${standardPrice.toLocaleString()}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Promotional Price</span><span>${promoPrice.toLocaleString()}</span></div>
                    <div className="flex justify-between font-bold"><span>Contract Total</span><span style={{ color: brandColor }}>${chosenPrice.toLocaleString()}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Payment Method</span><span>{selectedPayment === "financing" ? "Financing" : "Cash / Check"}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Deposit</span><span>{depositAmount === 0 ? "$0 Down" : `$${depositAmount.toLocaleString()}`}</span></div>
                  </div>
                </div>
                <div className="pb-1">
                  <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">Photos</p>
                  <div className="flex items-center gap-2 text-xs"><CheckCircle className="h-3.5 w-3.5 text-emerald-600" /><span>Project photos captured and attached</span></div>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">Signatures</p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      {homeownerSig ? (
                        <img src={homeownerSig} alt="Homeowner signature" className="h-8 object-contain" />
                      ) : (
                        <CheckCircle className="h-3.5 w-3.5 text-emerald-600" />
                      )}
                      <span className="text-xs">{arrival.name} — {homeownerSig ? "Signed" : "Pending"}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {repSig ? (
                        <img src={repSig} alt="Rep signature" className="h-8 object-contain" />
                      ) : (
                        <CheckCircle className="h-3.5 w-3.5 text-emerald-600" />
                      )}
                      <span className="text-xs">Company Rep — {repSig ? "Signed" : "Pending"}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="rounded-xl border p-4 max-w-sm mx-auto text-left space-y-2 mb-5 bg-muted/20">
              <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">Contract Package Includes</p>
              <div className="flex items-center gap-2 text-xs"><Check className="h-3.5 w-3.5 shrink-0" style={{ color: brandColor }} /><span>Agreement (Terms & Conditions)</span></div>
              <div className="flex items-center gap-2 text-xs"><Check className="h-3.5 w-3.5 shrink-0" style={{ color: brandColor }} /><span>Work Order — Full build breakdown & materials</span></div>
              <div className="flex items-center gap-2 text-xs"><Check className="h-3.5 w-3.5 shrink-0" style={{ color: brandColor }} /><span>Project Photos</span></div>
              <div className="flex items-center gap-2 text-xs"><Check className="h-3.5 w-3.5 shrink-0" style={{ color: brandColor }} /><span>Final Pricing Summary</span></div>
            </div>

            <div className="rounded-xl border p-4 max-w-sm mx-auto text-left space-y-3 mb-5">
              <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">What Happens Next</p>
              <div className="flex items-start gap-3"><CalendarCheck className="h-4 w-4 mt-0.5 shrink-0" style={{ color: brandColor }} /><p className="text-xs">We'll contact you within 24–48 hours to schedule your installation date.</p></div>
              <div className="flex items-start gap-3"><FileText className="h-4 w-4 mt-0.5 shrink-0" style={{ color: brandColor }} /><p className="text-xs">A copy of your agreement will be sent to your email on file.</p></div>
              <div className="flex items-start gap-3"><Phone className="h-4 w-4 mt-0.5 shrink-0" style={{ color: brandColor }} /><p className="text-xs">Questions? Call us anytime — we're here to help.</p></div>
            </div>

            <Card className="max-w-sm mx-auto border-amber-500/30 bg-amber-500/5 mb-5">
              <CardContent className="p-3 text-xs text-amber-700 flex items-start gap-2">
                <Shield className="h-4 w-4 shrink-0 mt-0.5" />
                <span>Build configuration, pricing, and materials are now locked. Any changes require a Change Order.</span>
              </CardContent>
            </Card>

            <p className="text-lg font-bold" style={{ color: brandColor }}>Thank you for choosing {branding.companyName}!</p>
            <Button variant="outline" size="sm" className="mt-4" onClick={() => { setSuccessModalOpen(false); onClose(); }}>Done</Button>
          </div>
        </DialogContent>
      </Dialog>

      <SendContractDialog
        open={sendDialogOpen}
        onOpenChange={setSendDialogOpen}
        customerName={arrival.name}
        customerEmail={arrival.primaryEmail || lead.primaryEmail}
        contractId={`CTR-${Date.now()}`}
        companyName={branding.companyName}
      />
    </Dialog>
  );
}
